/**
 * Medición de un modelo IFC para OPUS ci: lo que convierte un archivo BIM en cantidades de obra.
 *
 * Se separó de `IfcViewer3D.tsx` (que solo dibuja) para poder probarlo y compararlo contra una vara.
 * Tiene dos partes:
 *  1. PURA (sin web-ifc ni three): unidades, elección de la cantidad declarada, medidas geométricas,
 *     sumas por tipo. Probada en `medicionIfc.test.ts`.
 *  2. ADAPTADOR (`crearMedidor`): lee el modelo con la API de web-ifc y usa la parte pura. Lo usan el
 *     visor y el banco de prueba (`scratchpad/aplus-ifc/medir.html`), así los dos miden igual.
 *
 * Nada de esto usa tablas de nombres: las magnitudes salen del campo TIPADO de cada cantidad
 * (AreaValue/LengthValue/VolumeValue/WeightValue) y cuál de varias áreas mide "el muro" se decide contrastando
 * contra la geometría real de la pieza, no por el `Name` de la cantidad.
 */
import {
  MAX_MATERIALES_A_CLASIFICAR,
  PARAMETROS_MEDICION_IFC_DEFAULT,
  claseCanonica,
  densidadDeFamiliaIfc,
  familiaDeTipoDeMaterial,
  normalizarNombreDeMaterial,
  pareceIdentificadorIfc,
  type FamiliaMaterialIfc,
  type MaterialDeducidoIfc,
  type ParametrosMedicionIfc,
  type PlanoInventario,
  type TipoMaterialIfc,
} from '@supernova/shared';
import { MAGNITUDES_QTO_DEL_ESTANDAR } from './qtoDelEstandarIfc.js';

/*
 * QUÉ se inventaría: todo IfcElement del modelo, con sus subclases según el ESQUEMA que trae web-ifc
 * (`InheritanceDef`), no una lista de clases (ver `crearMedidor` → `inventariar`). El nombre visible sale
 * del propio modelo (el `Name` del tipo); la clase cruda se conserva como etiqueta secundaria.
 */

type Capa = PlanoInventario['capas'][number];
type Instancia = NonNullable<Capa['instancias']>[number];

// =====================================================================================================
// 1. PARTE PURA
// =====================================================================================================

// --- Unidades -----------------------------------------------------------------------------------------

/** Prefijos del SI (norma, no dato de obra). */
export const PREFIJO_SI: Readonly<Record<string, number>> = {
  EXA: 1e18, PETA: 1e15, TERA: 1e12, GIGA: 1e9, MEGA: 1e6, KILO: 1e3,
  HECTO: 1e2, DECA: 1e1, DECI: 1e-1, CENTI: 1e-2, MILLI: 1e-3,
  MICRO: 1e-6, NANO: 1e-9, PICO: 1e-12, FEMTO: 1e-15, ATTO: 1e-18,
};

/** Una unidad del modelo (IfcSIUnit o IfcConversionBasedUnit), ya leída a datos planos. */
export interface UnidadIfc {
  /** UnitType del IFC: 'LENGTHUNIT' | 'AREAUNIT' | 'VOLUMEUNIT' | … */
  tipo: string;
  /** Prefix del IfcSIUnit ('MILLI', 'CENTI', …); ausente = sin prefijo. */
  prefijo?: string;
  /** IfcConversionBasedUnit (pulgada, pie²…): valor en la unidad base y el prefijo de esa base. */
  conversion?: { valor: number; prefijoBase?: string };
  /** IfcDerivedUnit (kg/m³…): sus unidades componentes con su exponente. */
  elementos?: { unidad: UnidadIfc; exponente: number }[];
}

/** Factores para llevar a metros (m, m², m³) lo que viene en las unidades del modelo. */
export interface FactoresUnidad {
  longitud: number;
  area: number;
  volumen: number;
  /** A kilogramos (MASSUNIT; sin declarar = kg, la base del SI). */
  masa: number;
  /** A kg/m³ (MASSDENSITYUNIT; sin declarar = kg/m³). */
  densidad: number;
  /** false si el modelo no declara unidad de longitud (se asumió metro). */
  longitudDeclarada: boolean;
}

function factorDeUnidad(u: UnidadIfc, potencia: 1 | 2 | 3): number {
  if (u.conversion && u.conversion.valor > 0) {
    const base = u.conversion.prefijoBase ? PREFIJO_SI[u.conversion.prefijoBase] ?? 1 : 1;
    return u.conversion.valor * Math.pow(base, potencia);
  }
  const p = u.prefijo ? PREFIJO_SI[u.prefijo] ?? 1 : 1;
  return Math.pow(p, potencia);
}

/**
 * Área y volumen tienen su PROPIA unidad en IFC. Un Revit con longitudes en MILÍMETROS declara las
 * áreas en m² y los volúmenes en m³: convertir el área con (factor de longitud)² la achicaba un millón
 * de veces (bug medido en BasicHouse). Solo si el modelo no declara unidad de área/volumen se deriva
 * de la de longitud.
 */
export function factoresDeUnidades(unidades: readonly UnidadIfc[]): FactoresUnidad {
  const de = (t: string) => unidades.find((u) => u.tipo === t);
  const lu = de('LENGTHUNIT');
  const au = de('AREAUNIT');
  const vu = de('VOLUMEUNIT');
  const longitud = lu ? factorDeUnidad(lu, 1) : 1;
  const mu = de('MASSUNIT');
  // La unidad de masa del SI se escribe en GRAMOS con prefijo (KILO GRAM = kg): de ahí el 1e-3.
  const aKg = (u: UnidadIfc) => factorDeUnidad(u, 1) * 1e-3;
  const du = de('MASSDENSITYUNIT');
  let densidad = 1;
  if (du?.elementos?.length) {
    for (const { unidad: e, exponente } of du.elementos) {
      const f =
        e.tipo === 'MASSUNIT' ? aKg(e) : e.tipo === 'VOLUMEUNIT' ? factorDeUnidad(e, 3) : e.tipo === 'LENGTHUNIT' ? factorDeUnidad(e, 1) : 1;
      densidad *= Math.pow(f, exponente);
    }
  }
  return {
    longitud,
    area: au ? factorDeUnidad(au, 2) : longitud * longitud,
    volumen: vu ? factorDeUnidad(vu, 3) : longitud * longitud * longitud,
    masa: mu ? aKg(mu) : 1,
    densidad: Number.isFinite(densidad) && densidad > 0 ? densidad : 1,
    longitudDeclarada: lu != null,
  };
}

// --- Nombres y agrupación -----------------------------------------------------------------------------

/**
 * Colapsa espacios repetidos (Revit exporta "Familia:2  TIPO" con espacios dobles) para que el nombre
 * venido de la relación de tipo y el deducido de la instancia converjan en la MISMA clave.
 */
export const normNombre = (s: string): string => s.replace(/\s+/g, ' ').trim();

/**
 * El nombre de instancia de Revit viene "Familia:Tipo:id[:sub]"; el tipo es sin los ids del final
 * (pueden venir dos, "…:214377:2"), así que se quitan TODOS los grupos ":número".
 */
export const tipoDesdeInstancia = (nm: string): string => normNombre(nm.replace(/(:\s*\d+\s*)+$/, ''));

/** Uno → ese; varios → `varios`; ninguno → null. */
export const colapsar = (nombres: Set<string>, varios: string): string | null =>
  nombres.size === 0 ? null : nombres.size === 1 ? [...nombres][0]! : varios;

export const r2 = (x: number) => Math.round(x * 100) / 100;
export const r3 = (x: number) => Math.round(x * 1000) / 1000;

// --- Cantidades declaradas ----------------------------------------------------------------------------

export type Magnitud = 'area' | 'longitud' | 'volumen';

/** Una cantidad física declarada por el modelo (IfcQuantityArea/Length/Volume/Weight), YA en m / m² / m³ / kg. */
export interface CantidadDeclarada {
  nombre: string;
  magnitud: Magnitud | 'peso';
  valor: number;
}

export interface Medidas {
  area?: number;
  longitud?: number;
  volumen?: number;
}

/** Lo que se marca como estimado de una pieza: las tres magnitudes geométricas y el peso. */
export type MagnitudEstimable = Magnitud | 'peso';

/**
 * Tolerancia (por magnitud) para decir que una cantidad declarada mide LO MISMO que la geometría.
 * Es un UMBRAL: viene de la calibración del motor (`ifc_ventana_*`, panel staff), no del código. Los
 * valores y su porqué (medidos contra AC20-FZK-Haus) están en @supernova/shared/parametrosMedicionIfc.
 */
export type VentanaPlausible = Readonly<Record<Magnitud, number>>;

/** Las tolerancias por magnitud a partir de los parámetros de la calibración. */
export function ventanaDe(p: ParametrosMedicionIfc): VentanaPlausible {
  return { area: p.ifc_ventana_area, longitud: p.ifc_ventana_longitud, volumen: p.ifc_ventana_volumen };
}

const esNeta = (nombre: string) => /net/i.test(nombre);

/**
 * De las cantidades que declara el modelo, elige UNA por magnitud.
 *
 * Un elemento suele declarar varias áreas (ArchiCAD: huella, cara lateral neta y bruta, cara interior,
 * exterior, cantos…). Antes se tomaba la primera y una "neta" la reemplazaba → en un muro de FZK-Haus
 * salía la HUELLA (1,0 m²) en vez de la CARA (10,4 m²). Ahora, contra la geometría real de la pieza:
 *   1. solo cuentan las que miden lo mismo (±`ventana` de su magnitud, de la calibración); si ninguna,
 *      no se elige nada (una losa declara su perímetro y el de su vano como "longitudes": no son su
 *      largo) y `medidasDeLaPieza` la mide de la malla, marcada como estimada;
 *   2. de esas, la más cercana. La malla ya trae los vanos descontados, así que la más cercana es la
 *      neta sin preguntarle el nombre; "neta" solo desempata valores idénticos.
 * Sin geometría con qué contrastar, el criterio anterior (la primera; una neta la reemplaza).
 */
export function elegirCantidades(
  cantidades: readonly CantidadDeclarada[],
  ref: Medidas | null,
  ventana: VentanaPlausible,
): Medidas {
  const out: Medidas = {};
  for (const mag of ['area', 'longitud', 'volumen'] as const) {
    const cands = cantidades.filter((q) => q.magnitud === mag && Number.isFinite(q.valor) && q.valor > 0);
    if (cands.length === 0) continue;
    const r = ref?.[mag];
    if (r == null || !(r > 0)) {
      out[mag] = (cands.find((q) => esNeta(q.nombre)) ?? cands[0])!.valor;
      continue;
    }
    const lejania = (v: number) => Math.abs(Math.log(v / r));
    const plausibles = cands.filter((q) => lejania(q.valor) <= Math.log(ventana[mag]));
    if (!plausibles.length) continue;
    out[mag] = plausibles.reduce((a, b) => {
      const da = lejania(a.valor), db = lejania(b.valor);
      if (db < da - 1e-12) return b;
      if (Math.abs(db - da) <= 1e-12 && esNeta(b.nombre) && !esNeta(a.nombre)) return b;
      return a;
    }).valor;
  }
  return out;
}

/** El motivo, para la persona, de una pieza sin volumen ni peso porque su malla no es un sólido. */
export const MOTIVO_SIN_SOLIDO = 'sin volumen: el modelo no la trae como sólido';

// --- Medidas geométricas ------------------------------------------------------------------------------

/**
 * Lo que se va juntando de la malla de UN elemento (puede tener varias piezas colocadas), en
 * coordenadas del modelo. El eje vertical es Y (web-ifc entrega el modelo con Y hacia arriba).
 */
export interface AcumuladoGeom {
  /** Área de triángulos por dirección de normal (agrupada): para hallar la cara grande. */
  areaPorNormal: Map<string, number>;
  areaTotal: number;
  /** 6 × volumen con signo (tetraedros contra el origen). */
  vol6: number;
  yMin: number;
  yMax: number;
  /** Puntos (x,z intercalados) de los cascos convexos en planta de cada pieza. */
  planta: number[];
  /**
   * Vértices colocados (x,y,z intercalados) de todas sus piezas: para la caja ORIENTADA del elemento
   * (el largo de un cabrio inclinado no se ve en planta ni en alzado). Se descarta al cerrar.
   */
  puntos: number[];
  /**
   * Triángulos NO verticales proyectados en planta (x1,z1,x2,z2,x3,z3 intercalados): su unión es la
   * huella REAL de la pieza (no su casco convexo), de donde sale el recorrido de una pieza en L, en U o
   * curva (`recorridoDeLaPieza`). Se descarta al cerrar.
   */
  trisPlanta: number[];
  /**
   * Por cada triángulo de `trisPlanta`, su área en planta y su PENDIENTE en planta (el gradiente de la
   * altura, dy/dx y dy/dz) multiplicada por esa área: (a, a·gx, a·gz) intercalados. Ponderar por el área en
   * planta acota a las astillas casi verticales (su gradiente es enorme pero su área ~0). Sirve para
   * distinguir, entre dos tramos paralelos de la huella, un recorrido que va y vuelve (un barandal de
   * escalera de ida y vuelta: un tramo sube hacia un lado y el otro hacia el otro) de una pieza doble (una
   * cercha de doble ángulo, un pasamanos junto a la baranda: los dos tramos corren a la par, con la misma
   * pendiente). Ver `recorridosPorPendiente`. Se descarta al cerrar.
   */
  pendPlanta: number[];
  /**
   * Pendiente media del recorrido: Σ(área en planta × largo/largo en planta de la arista mayor EN PLANTA de
   * cada triángulo no vertical) y Σ(área en planta). En un barandal de escalera las aristas largas corren a lo
   * largo de los pasamanos inclinados; en uno plano, horizontales (cociente 1).
   */
  pendNum: number;
  pendDen: number;
  /** Suma de los perímetros en planta de esos triángulos: cota del perímetro de su unión. */
  perimetroPlanta: number;
  /**
   * Alguna de sus mallas NO es un sólido cerrado (`esSolidoCerrado`): una superficie suelta no encierra
   * volumen, y su volumen queda sin medir en vez de inventarse.
   */
  abierta: boolean;
}

export function nuevoAcumulado(): AcumuladoGeom {
  return {
    areaPorNormal: new Map(),
    areaTotal: 0,
    vol6: 0,
    yMin: Infinity,
    yMax: -Infinity,
    planta: [],
    puntos: [],
    trisPlanta: [],
    pendPlanta: [],
    pendNum: 0,
    pendDen: 0,
    perimetroPlanta: 0,
    abierta: false,
  };
}

/**
 * Un triángulo cuenta para la huella en planta si no es vertical. La tolerancia es NUMÉRICA (el seno del
 * ángulo con la vertical: 1e-6 rad), no un dato de obra: una cara vertical sale de web-ifc con un error de
 * redondeo que la inclina una millonésima, y su proyección (una astilla sin área) solo ensucia la unión.
 */
const NO_VERTICAL_MIN = 1e-6;

/** Normales agrupadas cada ~3° (1/20 rad): los triángulos de una misma cara plana caen juntos. */
const PASOS_NORMAL = 20;
const claveNormal = (x: number, y: number, z: number) =>
  `${Math.round(x * PASOS_NORMAL)},${Math.round(y * PASOS_NORMAL)},${Math.round(z * PASOS_NORMAL)}`;
const claveOpuesta = (k: string) =>
  k
    .split(',')
    .map((n) => String(-Number(n) || 0))
    .join(',');

/**
 * Suma una malla al elemento. `pos` = posiciones locales (x,y,z), `idx` = triángulos, `t` = matriz
 * 4×4 de colocación en orden de columnas (la `flatTransformation` de web-ifc).
 */
export function acumularMalla(
  acc: AcumuladoGeom,
  pos: ArrayLike<number>,
  idx: ArrayLike<number>,
  t: ArrayLike<number>,
): void {
  const wp = new Float64Array(pos.length);
  const xz: number[] = [];
  for (let k = 0; k < pos.length; k += 3) {
    const x = pos[k]!, y = pos[k + 1]!, z = pos[k + 2]!;
    const X = t[0]! * x + t[4]! * y + t[8]! * z + t[12]!;
    const Y = t[1]! * x + t[5]! * y + t[9]! * z + t[13]!;
    const Z = t[2]! * x + t[6]! * y + t[10]! * z + t[14]!;
    wp[k] = X;
    wp[k + 1] = Y;
    wp[k + 2] = Z;
    if (Y < acc.yMin) acc.yMin = Y;
    if (Y > acc.yMax) acc.yMax = Y;
    xz.push(X, Z);
    acc.puntos.push(X, Y, Z);
  }
  for (let i = 0; i + 2 < idx.length; i += 3) {
    const a = idx[i]! * 3, b = idx[i + 1]! * 3, c = idx[i + 2]! * 3;
    const ax = wp[a]!, ay = wp[a + 1]!, az = wp[a + 2]!;
    const ux = wp[b]! - ax, uy = wp[b + 1]! - ay, uz = wp[b + 2]! - az;
    const vx = wp[c]! - ax, vy = wp[c + 1]! - ay, vz = wp[c + 2]! - az;
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz);
    const bx = wp[b]!, by = wp[b + 1]!, bz = wp[b + 2]!;
    const cx = wp[c]!, cy = wp[c + 1]!, cz = wp[c + 2]!;
    if (len > 0) {
      const area = len / 2;
      acc.areaTotal += area;
      const k = claveNormal(nx / len, ny / len, nz / len);
      acc.areaPorNormal.set(k, (acc.areaPorNormal.get(k) ?? 0) + area);
      if (Math.abs(ny) / len > NO_VERTICAL_MIN) {
        // Huella en planta (x,z) y pendiente de su arista mayor.
        acc.trisPlanta.push(ax, az, bx, bz, cx, cz);
        // área en planta |ny|/2 y gradiente (−nx/ny, −nz/ny) × esa área = −signo(ny)·(nx, nz)/2
        const sg = ny > 0 ? -0.5 : 0.5;
        acc.pendPlanta.push(Math.abs(ny) / 2, sg * nx, sg * nz);
        acc.perimetroPlanta += Math.hypot(bx - ax, bz - az) + Math.hypot(cx - bx, cz - bz) + Math.hypot(ax - cx, az - cz);
        const areaPlanta = Math.abs(ny) / 2;
        // La arista más larga EN PLANTA marca hacia dónde corre el triángulo; su largo real ÷ su largo en
        // planta es su pendiente (sec α). Se elige por la planta, no por el largo real: en una astilla casi
        // vertical la arista más larga es la vertical y su "pendiente" sería infinita.
        let e3 = 0, eP = 0;
        const aristas = [ax, ay, az, bx, by, bz, bx, by, bz, cx, cy, cz, cx, cy, cz, ax, ay, az];
        for (let q = 0; q < 18; q += 6) {
          const dx = aristas[q + 3]! - aristas[q]!, dy = aristas[q + 4]! - aristas[q + 1]!, dz = aristas[q + 5]! - aristas[q + 2]!;
          const lp = Math.hypot(dx, dz);
          if (lp > eP) {
            eP = lp;
            e3 = Math.hypot(dx, dy, dz);
          }
        }
        if (eP > 0) {
          acc.pendNum += (areaPlanta * e3) / eP;
          acc.pendDen += areaPlanta;
        }
      }
    }
    acc.vol6 += ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx);
  }
  const casco = cascoConvexo(xz);
  for (const v of casco) acc.planta.push(v);
  if (!esSolidoCerrado(wp, idx)) acc.abierta = true;
}

/**
 * ¿La malla ENCIERRA un volumen? Criterio estructural (no por clase): la suma de los vectores de área de
 * sus triángulos es cero en una superficie cerrada, y solo entonces el volumen con signo (tetraedros contra
 * un origen) no depende del origen elegido, o sea, es un volumen. En una superficie suelta —el
 * IfcFaceBasedSurfaceModel de un barandal de Revit, una zapata exportada sin tapa— la suma queda en una
 * fracción visible del área (medido: 5e-4 a 0,76 de Σ|área|). No se exige que cada arista tenga pareja:
 * los sólidos de Revit y ArchiCAD traen aristas partidas (uniones en T) y están cerrados igual (las
 * ventanas de FZK-Haus). La tolerancia, 1e-9 de Σ|área|, es NUMÉRICA: un sólido cerrado da 1e-16.
 */
export function esSolidoCerrado(pos: ArrayLike<number>, idx: ArrayLike<number>): boolean {
  let sx = 0, sy = 0, sz = 0, total = 0;
  for (let t = 0; t + 2 < idx.length; t += 3) {
    const a = idx[t]! * 3, b = idx[t + 1]! * 3, c = idx[t + 2]! * 3;
    const ux = pos[b]! - pos[a]!, uy = pos[b + 1]! - pos[a + 1]!, uz = pos[b + 2]! - pos[a + 2]!;
    const vx = pos[c]! - pos[a]!, vy = pos[c + 1]! - pos[a + 1]!, vz = pos[c + 2]! - pos[a + 2]!;
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    sx += nx;
    sy += ny;
    sz += nz;
    total += Math.hypot(nx, ny, nz);
  }
  // Sin área no aporta superficie ni volumen: no abre la pieza.
  return total === 0 || Math.hypot(sx, sy, sz) <= 1e-9 * total;
}

/** Casco convexo 2D (cadena monótona). Entrada y salida: x,y intercalados. */
export function cascoConvexo(pts: readonly number[]): number[] {
  const n = pts.length / 2;
  if (n < 3) return pts.slice();
  const ids = Array.from({ length: n }, (_, i) => i).sort(
    (i, j) => pts[i * 2]! - pts[j * 2]! || pts[i * 2 + 1]! - pts[j * 2 + 1]!,
  );
  const cruz = (o: number, a: number, b: number) =>
    (pts[a * 2]! - pts[o * 2]!) * (pts[b * 2 + 1]! - pts[o * 2 + 1]!) -
    (pts[a * 2 + 1]! - pts[o * 2 + 1]!) * (pts[b * 2]! - pts[o * 2]!);
  const abajo: number[] = [];
  for (const i of ids) {
    while (abajo.length >= 2 && cruz(abajo[abajo.length - 2]!, abajo[abajo.length - 1]!, i) <= 0) abajo.pop();
    abajo.push(i);
  }
  const arriba: number[] = [];
  for (let q = ids.length - 1; q >= 0; q--) {
    const i = ids[q]!;
    while (arriba.length >= 2 && cruz(arriba[arriba.length - 2]!, arriba[arriba.length - 1]!, i) <= 0) arriba.pop();
    arriba.push(i);
  }
  const casco = abajo.slice(0, -1).concat(arriba.slice(0, -1));
  const out: number[] = [];
  for (const i of casco) out.push(pts[i * 2]!, pts[i * 2 + 1]!);
  return out;
}

/** Área de un polígono (x,y intercalados). */
export function areaPoligono(p: readonly number[]): number {
  let s = 0;
  const n = p.length / 2;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    s += p[i * 2]! * p[j * 2 + 1]! - p[j * 2]! * p[i * 2 + 1]!;
  }
  return Math.abs(s) / 2;
}

/**
 * Ejes principales de una nube de puntos 3D (x,y,z intercalados): autovectores de su covarianza
 * (Jacobi sobre la matriz simétrica 3×3), ordenados del eje de MAYOR dispersión al de menor. Son los
 * ejes de la caja ORIENTADA del elemento: un cabrio inclinado tiene su eje mayor a lo largo del cabrio,
 * no en horizontal ni en vertical.
 */
export function ejesPrincipales(pts: readonly number[]): [number, number, number][] {
  const n = pts.length / 3;
  if (n === 0) return [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  let mx = 0, my = 0, mz = 0;
  for (let i = 0; i < pts.length; i += 3) { mx += pts[i]!; my += pts[i + 1]!; mz += pts[i + 2]!; }
  mx /= n; my /= n; mz /= n;
  const a = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  for (let i = 0; i < pts.length; i += 3) {
    const d = [pts[i]! - mx, pts[i + 1]! - my, pts[i + 2]! - mz];
    for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) a[r]![c]! += d[r]! * d[c]!;
  }
  const v = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  for (let barrido = 0; barrido < 50; barrido++) {
    let p = 0, q = 1;
    for (const [i, j] of [[0, 1], [0, 2], [1, 2]] as const) if (Math.abs(a[i]![j]!) > Math.abs(a[p]![q]!)) { p = i; q = j; }
    const apq = a[p]![q]!;
    if (Math.abs(apq) <= 1e-15 * (Math.abs(a[p]![p]!) + Math.abs(a[q]![q]!) + 1e-300)) break;
    const theta = (a[q]![q]! - a[p]![p]!) / (2 * apq);
    const t = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
    const c = 1 / Math.sqrt(t * t + 1), sn = t * c;
    for (let k = 0; k < 3; k++) {
      const akp = a[k]![p]!, akq = a[k]![q]!;
      a[k]![p] = c * akp - sn * akq;
      a[k]![q] = sn * akp + c * akq;
    }
    for (let k = 0; k < 3; k++) {
      const apk = a[p]![k]!, aqk = a[q]![k]!;
      a[p]![k] = c * apk - sn * aqk;
      a[q]![k] = sn * apk + c * aqk;
    }
    for (let k = 0; k < 3; k++) {
      const vkp = v[k]![p]!, vkq = v[k]![q]!;
      v[k]![p] = c * vkp - sn * vkq;
      v[k]![q] = sn * vkp + c * vkq;
    }
  }
  const orden = [0, 1, 2].sort((i, j) => a[j]![j]! - a[i]![i]!);
  return orden.map((k) => [v[0]![k]!, v[1]![k]!, v[2]![k]!] as [number, number, number]);
}

/**
 * LARGO de la pieza a lo largo del eje MAYOR de su caja orientada, medido a media pieza: en cada uno
 * de los dos planos que contienen ese eje, área de la proyección (casco convexo) ÷ el ancho de su
 * rectángulo de área mínima; se toma el menor. Así un muro a inglete da su largo de eje (4,17 m en FZK-Haus, el `Length` del propio
 * modelo) y no el exterior (4,29), y un cabrio inclinado da su largo real (6,26 m) y no su
 * proyección horizontal (5,50). En piezas rectas coincide con el largo en planta de siempre.
 * Devuelve null si no hay puntos.
 */
export function largoOrientado(pts: readonly number[]): number | null {
  const n = pts.length / 3;
  if (n < 2) return null;
  const [u, v, w] = ejesPrincipales(pts);
  const dot = (i: number, e: readonly number[]) => pts[i]! * e[0]! + pts[i + 1]! * e[1]! + pts[i + 2]! * e[2]!;
  let minU = Infinity, maxU = -Infinity;
  const uv: number[] = [];
  const uw: number[] = [];
  for (let i = 0; i < pts.length; i += 3) {
    const pu = dot(i, u!);
    if (pu < minU) minU = pu;
    if (pu > maxU) maxU = pu;
    uv.push(pu, dot(i, v!));
    uw.push(pu, dot(i, w!));
  }
  const eu = maxU - minU;
  if (!(eu > 0)) return null;
  // En cada plano que contiene el eje mayor: casco de la proyección y su rectángulo de área mínima (el
  // que da la orientación EXACTA en ese plano, igual que en planta). Largo a media pieza = área ÷ ancho.
  // Un plano sin espesor (placa sin grosor) no da largo por área: queda el largo del rectángulo.
  const largos: number[] = [];
  for (const proy of [uv, uw]) {
    const casco = cascoConvexo(proy);
    const rect = rectanguloMinimo(casco);
    if (!(rect.largo > 0)) continue;
    largos.push(rect.ancho > rect.largo * 1e-9 ? areaPoligono(casco) / rect.ancho : rect.largo);
  }
  return largos.length ? Math.min(eu, ...largos) : eu;
}

/**
 * Rectángulo de área mínima que contiene un casco convexo: largo ≥ ancho, orientado como la pieza.
 * `eje` = dirección (unitaria) de su lado largo.
 */
export function rectanguloMinimo(casco: readonly number[]): { largo: number; ancho: number; eje: [number, number] } {
  const n = casco.length / 2;
  if (n === 0) return { largo: 0, ancho: 0, eje: [1, 0] };
  if (n < 3) {
    const dx = n === 2 ? casco[2]! - casco[0]! : 0, dy = n === 2 ? casco[3]! - casco[1]! : 0;
    const d = Math.hypot(dx, dy);
    return { largo: d, ancho: 0, eje: d > 0 ? [dx / d, dy / d] : [1, 0] };
  }
  let mejor = { area: Infinity, largo: 0, ancho: 0, eje: [1, 0] as [number, number] };
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    let ex = casco[j * 2]! - casco[i * 2]!, ey = casco[j * 2 + 1]! - casco[i * 2 + 1]!;
    const L = Math.hypot(ex, ey);
    if (L === 0) continue;
    ex /= L;
    ey /= L;
    let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
    for (let k = 0; k < n; k++) {
      const x = casco[k * 2]!, y = casco[k * 2 + 1]!;
      const u = x * ex + y * ey, v = -x * ey + y * ex;
      if (u < minU) minU = u;
      if (u > maxU) maxU = u;
      if (v < minV) minV = v;
      if (v > maxV) maxV = v;
    }
    const a = maxU - minU, b = maxV - minV;
    if (a * b < mejor.area) mejor = { area: a * b, largo: Math.max(a, b), ancho: Math.min(a, b), eje: a >= b ? [ex, ey] : [-ey, ex] };
  }
  return { largo: mejor.largo, ancho: mejor.ancho, eje: mejor.eje };
}

/**
 * Cuántas rectas por pieza se tiran para leer su huella (`cortesDeLaHuella`) y en cuántas direcciones
 * (`huellaPorRectas`). Son la RESOLUCIÓN del método numérico, como `PASOS_NORMAL`, no datos de obra: con
 * 256 rectas el error de una banda es de un paso en cada punta (±0,4 % del largo del casco); el tanteo
 * (64) solo decide si hace falta leer la huella entera; con 16 direcciones la fórmula de Crofton mide un
 * perímetro con −0,3 % / +0,2 % según cómo esté girado. Medido contra el largo que declara Revit en 13
 * barandales de Clinic: −0,9 % … +0,2 %.
 */
const RECTAS_POR_PIEZA = 256;
const RECTAS_DE_TANTEO = 64;
const DIRECCIONES_CROFTON = 16;

/**
 * Corta la huella en planta (la unión de los triángulos `tris`, x1,y1,x2,y2,x3,y3 intercalados) con
 * rectas paralelas, perpendiculares a `normal` y separadas `paso`. Por recta: en cuántos TRAMOS separados
 * la corta la huella (los triángulos que se tocan o se pisan forman un solo tramo) y cuánto largo cubre.
 * Devuelve la suma sobre todas las rectas. Es exacto en cada recta: no une polígonos (unir miles de
 * triángulos que se pisan —la cara de arriba y la de abajo de cada barrote— es frágil) ni rasteriza.
 */
export function cortesDeLaHuella(
  tris: readonly number[],
  normal: readonly [number, number],
  paso: number,
): { tramos: number; cubierto: number } {
  const [nx, ny] = normal;
  const nt = Math.floor(tris.length / 6);
  // Coordenadas de cada vértice en el sistema de las rectas: s (a través) y t (a lo largo).
  const S = new Float64Array(nt * 3), T = new Float64Array(nt * 3);
  let pmin = Infinity, pmax = -Infinity, tmin = Infinity, tmax = -Infinity;
  for (let v = 0; v < nt * 3; v++) {
    const x = tris[v * 2]!, y = tris[v * 2 + 1]!;
    const sv = x * nx + y * ny, tv = -x * ny + y * nx;
    S[v] = sv;
    T[v] = tv;
    if (sv < pmin) pmin = sv;
    if (sv > pmax) pmax = sv;
    if (tv < tmin) tmin = tv;
    if (tv > tmax) tmax = tv;
  }
  const nLineas = Math.max(1, Math.ceil((pmax - pmin) / paso));
  // Rango de rectas que corta cada triángulo.
  const desde = new Int32Array(nt), hasta = new Int32Array(nt);
  for (let i = 0; i < nt; i++) {
    const a = S[i * 3]!, b = S[i * 3 + 1]!, c = S[i * 3 + 2]!;
    const lo = a < b ? (a < c ? a : c) : b < c ? b : c;
    const hi = a > b ? (a > c ? a : c) : b > c ? b : c;
    desde[i] = Math.max(0, Math.ceil((lo - pmin) / paso - 0.5));
    hasta[i] = Math.min(nLineas - 1, Math.floor((hi - pmin) / paso - 0.5));
  }
  // Dos pasadas: cuántos tramos caen en cada recta, y después los tramos en un solo arreglo.
  const cuenta = new Int32Array(nLineas + 1);
  for (let i = 0; i < nt; i++) {
    for (let li = desde[i]!, h = hasta[i]!; li <= h; li++) cuenta[li + 1]!++;
  }
  for (let li = 0; li < nLineas; li++) cuenta[li + 1]! += cuenta[li]!;
  const lleno = cuenta.slice(0, nLineas);
  const A = new Float64Array(cuenta[nLineas]!), B = new Float64Array(cuenta[nLineas]!);
  for (let i = 0; i < nt; i++) {
    for (let li = desde[i]!, h = hasta[i]!; li <= h; li++) {
      const p = pmin + (li + 0.5) * paso;
      let a = Infinity, b = -Infinity;
      for (let k = 0; k < 3; k++) {
        const ia = i * 3 + k, ib = i * 3 + ((k + 1) % 3);
        const sa = S[ia]!, sb = S[ib]!;
        if ((sa - p) * (sb - p) > 0 || sa === sb) continue;
        const tc = T[ia]! + ((p - sa) / (sb - sa)) * (T[ib]! - T[ia]!);
        if (tc < a) a = tc;
        if (tc > b) b = tc;
      }
      const j = lleno[li]!++;
      A[j] = b >= a ? a : NaN;
      B[j] = b >= a ? b : NaN;
    }
  }
  // Por recta: fundir los tramos que se tocan, barriendo los comienzos y los finales ordenados por separado
  // (ordenar números sueltos es nativo y rápido; el resultado es el mismo que ordenar los pares). Tolerancia
  // NUMÉRICA: el borde que comparten dos triángulos vecinos da el mismo punto hasta el redondeo.
  const tol = 1e-9 * Math.max(1, tmax - tmin);
  let tramos = 0;
  let cubierto = 0;
  for (let li = 0; li < nLineas; li++) {
    const d = cuenta[li]!, h = cuenta[li + 1]!;
    if (h <= d) continue;
    const ini = A.subarray(d, h).sort(), fin = B.subarray(d, h).sort();
    // Los NaN (rectas que solo rozan un vértice) quedan al final de cada arreglo ordenado.
    let n = h - d;
    while (n > 0 && ini[n - 1] !== ini[n - 1]) n--;
    if (!n) continue;
    let i = 0, j = 0, abiertos = 0, desdeT = 0;
    while (i < n || j < n) {
      if (i < n && ini[i]! <= fin[j]! + tol) {
        if (abiertos++ === 0) {
          tramos++;
          desdeT = ini[i]!;
        }
        i++;
      } else {
        if (--abiertos === 0) cubierto += fin[j]! - desdeT;
        j++;
      }
    }
  }
  return { tramos, cubierto };
}

/**
 * Área y perímetro de la huella REAL en planta (la unión de los triángulos, no su casco convexo: un
 * barandal en L tiene la huella de una banda en L, no la del triángulo que la encierra), leídos con
 * rectas en `DIRECCIONES_CROFTON` direcciones a partir de `eje`: el área por Cavalieri (paso × lo
 * cubierto) y el perímetro por la fórmula de Cauchy–Crofton (P = ½ ∫∫ cruces). null si no hay triángulos.
 */
export function huellaPorRectas(
  tris: readonly number[],
  eje: readonly [number, number],
  paso: number,
): { area: number; perimetro: number } | null {
  if (tris.length < 6 || !(paso > 0)) return null;
  const a0 = Math.atan2(eje[1], eje[0]);
  let area = 0, cruces = 0;
  for (let k = 0; k < DIRECCIONES_CROFTON; k++) {
    const a = a0 + (k * Math.PI) / DIRECCIONES_CROFTON;
    const c = cortesDeLaHuella(tris, [Math.cos(a), Math.sin(a)], paso);
    area += c.cubierto * paso;
    cruces += 2 * c.tramos * paso;
  }
  return { area: area / DIRECCIONES_CROFTON, perimetro: (Math.PI / DIRECCIONES_CROFTON) * cruces * 0.5 };
}

/**
 * El rectángulo de igual área y perímetro que una huella (largo ≥ ancho): largo + ancho = P/2,
 * largo × ancho = A. En una banda delgada —en L, en U, curva— su largo es el de su eje (el recorrido);
 * en una figura compacta el discriminante se anula. En un rectángulo da sus lados exactos.
 */
export function rectanguloEquivalente(area: number, perimetro: number): { largo: number; ancho: number } {
  const q = perimetro / 4;
  const r = Math.sqrt(Math.max(0, q * q - area));
  return { largo: q + r, ancho: q - r };
}

/**
 * RECORRIDO de una pieza. Las otras medidas del largo (`medidasGeometricas`) miden una CUERDA (el tramo
 * mayor): un barandal en L, en U o el de una escalera que sube dando vueltas salía hasta 60 % corto.
 *  1. Se corta la huella real en planta con perpendiculares al eje de su casco: ∫ tramos = largo de los
 *     TRAMOS que corren a lo largo del eje, y lo cubierto ÷ eso = el ancho de cada tramo.
 *  2. Si el casco es angosto para ese ancho (menos de `ifc_forma_lineal_min` anchos: no cabe una vuelta
 *     que cuente), la pieza es recta y su largo es la cuerda: null. Dos tramos paralelos —un barandal que
 *     va y vuelve, o una cercha de doble ángulo— no se distinguen en planta: solo en un barandal
 *     (`puedeVolver`) y solo si suben hacia lados opuestos se cuentan como dos (`recorridosPorPendiente`).
 *  3. Si no (la huella es delgada dentro de un casco ancho: una L, una U, una escalera), el largo del
 *     rectángulo equivalente de la huella (`huellaPorRectas`) por la pendiente media de sus aristas
 *     (`pendNum`/`pendDen`), y solo si la huella es una BANDA: que ese rectángulo sea lineal con el mismo
 *     umbral de la calibración. Una losa con hueco no es una banda.
 * null si no hay huella o si no puede superar la cuerda: el recorrido no pasa de medio perímetro de la
 * huella, y ese perímetro no pasa ni de la suma de los de sus triángulos ni de la suma de sus dos
 * proyecciones. Son cotas (no umbrales): ahorran leer entera la huella de una columna o una cantonera.
 * En unidades de la malla.
 */
export function recorridoDeLaPieza(
  acc: AcumuladoGeom,
  casco: { largo: number; ancho: number; eje: readonly [number, number] },
  formaLinealMin: number,
  cuerda: number,
  /**
   * La pieza es un RECORRIDO por definición del esquema (IfcRailing: una baranda a lo largo de un camino;
   * su cantidad estándar es el largo de ese camino). Solo entonces dos tramos paralelos pueden ser ida y
   * vuelta. En una viga o un miembro no: un joist de cuerdas de doble ángulo con su varilla en zigzag tiene
   * tramos paralelos que suben y bajan, y su largo sigue siendo la luz (medido: +7 % en NIST si se cuenta).
   */
  puedeVolver = false,
): number | null {
  if (acc.trisPlanta.length < 6 || !(casco.largo > 0)) return null;
  const pendiente = acc.pendDen > 0 ? acc.pendNum / acc.pendDen : 1;
  if ((acc.perimetroPlanta / 2) * pendiente <= cuerda) return null;
  const tanteo = casco.largo / RECTAS_DE_TANTEO;
  const c = cortesDeLaHuella(acc.trisPlanta, casco.eje, tanteo);
  const largoTramos = c.tramos * tanteo;
  if (!(largoTramos > 0)) return null;
  const anchoTramo = (c.cubierto * tanteo) / largoTramos;
  if (!(anchoTramo > 0)) return null;
  // Casco angosto para el ancho del tramo: no cabe una vuelta que cuente en planta. La pieza es recta, salvo
  // que sus tramos paralelos sean IDA Y VUELTA: en planta un barandal de escalera que sube un tramo y vuelve
  // por el otro y una cercha de doble ángulo se ven igual (dos bandas juntas), pero en el espacio no: los
  // tramos del barandal suben hacia lados opuestos y los dos ángulos de la cercha corren a la par.
  if (casco.ancho / anchoTramo < formaLinealMin) {
    if (!puedeVolver || largoTramos <= casco.largo || acc.pendPlanta.length * 2 !== acc.trisPlanta.length) return null;
    const idaYVuelta = recorridosPorPendiente(acc.trisPlanta, acc.pendPlanta, casco.eje, casco.largo / RECTAS_POR_PIEZA, anchoTramo / casco.largo);
    return idaYVuelta * pendiente > cuerda ? idaYVuelta * pendiente : null;
  }
  // Cota del medio perímetro: un contorno no es más largo que la suma de sus dos proyecciones (lo que la
  // huella recorre a lo largo del eje y a lo ancho). Si ni así supera lo ya medido —una cantonera vertical,
  // cuya huella en L mide centímetros—, no hace falta leerla entera.
  const aLoAncho = cortesDeLaHuella(acc.trisPlanta, [-casco.eje[1], casco.eje[0]], tanteo).tramos * tanteo;
  if ((largoTramos + aLoAncho) * pendiente <= cuerda) return null;
  const paso = casco.largo / RECTAS_POR_PIEZA;
  const h = huellaPorRectas(acc.trisPlanta, casco.eje, paso);
  if (!h) return null;
  const eq = rectanguloEquivalente(h.area, h.perimetro);
  if (!(eq.ancho > 0) || eq.largo / eq.ancho < formaLinealMin) return null;
  return eq.largo * pendiente;
}

/**
 * Largo EN PLANTA de los recorridos de una pieza recta cuyos tramos paralelos pueden ir y volver. Se corta
 * la huella con perpendiculares al eje (como `cortesDeLaHuella`) y en cada recta se cuentan sus tramos por
 * la PENDIENTE con que corren a lo largo del eje (media ponderada por el área en planta, de `pendPlanta`):
 * subiendo, bajando o a nivel. Los tramos con la misma pendiente son la sección de UNA pieza (los dos ángulos
 * de una cercha, el pasamanos junto a la baranda): cuentan una vez. Los de pendientes opuestas son dos
 * recorridos (el tramo que sube y el que vuelve). Devuelve Σ recorridos × paso.
 * "A nivel" = que a lo largo de toda la pieza (`largo` del casco) suba menos que el ancho de su propio
 * tramo: `planoMax` = ancho del tramo ÷ largo del casco. Es una cota de la propia pieza, no un umbral de obra:
 * el redondeo de la exportación inclina una viga horizontal en millonésimas.
 */
export function recorridosPorPendiente(
  tris: readonly number[],
  pend: readonly number[],
  eje: readonly [number, number],
  paso: number,
  planoMax: number,
): number {
  const [ex, ey] = eje;
  // las rectas de corte son perpendiculares al eje: su normal es el eje mismo
  const nt = Math.floor(tris.length / 6);
  if (!nt || !(paso > 0)) return 0;
  let pmin = Infinity, pmax = -Infinity;
  for (let v = 0; v < nt * 3; v++) {
    const sv = tris[v * 2]! * ex + tris[v * 2 + 1]! * ey;
    if (sv < pmin) pmin = sv;
    if (sv > pmax) pmax = sv;
  }
  const nLineas = Math.max(1, Math.ceil((pmax - pmin) / paso));
  const porLinea: { a: number; b: number; w: number; g: number }[][] = Array.from({ length: nLineas }, () => []);
  for (let i = 0; i < nt; i++) {
    const S: number[] = [], T: number[] = [];
    for (let k = 0; k < 3; k++) {
      const x = tris[i * 6 + k * 2]!, y = tris[i * 6 + k * 2 + 1]!;
      S.push(x * ex + y * ey);
      T.push(-x * ey + y * ex);
    }
    const w = pend[i * 3]!;
    const g = pend[i * 3 + 1]! * ex + pend[i * 3 + 2]! * ey; // a · (dy/dt) a lo largo del eje
    const lo = Math.min(S[0]!, S[1]!, S[2]!), hi = Math.max(S[0]!, S[1]!, S[2]!);
    const desde = Math.max(0, Math.ceil((lo - pmin) / paso - 0.5));
    const hasta = Math.min(nLineas - 1, Math.floor((hi - pmin) / paso - 0.5));
    for (let li = desde; li <= hasta; li++) {
      const p = pmin + (li + 0.5) * paso;
      let a = Infinity, b = -Infinity;
      for (let k = 0; k < 3; k++) {
        const sa = S[k]!, sb = S[(k + 1) % 3]!;
        if ((sa - p) * (sb - p) > 0 || sa === sb) continue;
        const tc = T[k]! + ((p - sa) / (sb - sa)) * (T[(k + 1) % 3]! - T[k]!);
        if (tc < a) a = tc;
        if (tc > b) b = tc;
      }
      if (b >= a) porLinea[li]!.push({ a, b, w, g });
    }
  }
  let total = 0;
  for (const tramos of porLinea) {
    if (!tramos.length) continue;
    tramos.sort((u, v) => u.a - v.a);
    const clases = new Set<number>();
    let fin = -Infinity, w = 0, g = 0;
    const cerrar = () => {
      if (w > 0) {
        const m = g / w;
        clases.add(Math.abs(m) <= planoMax ? 0 : Math.sign(m));
      }
    };
    for (const t of tramos) {
      if (t.a > fin) {
        cerrar();
        w = 0;
        g = 0;
      }
      fin = Math.max(fin, t.b);
      w += t.w;
      g += t.g;
    }
    cerrar();
    total += clases.size * paso;
  }
  return total;
}

// La fracción mínima de "cara plana" (por debajo, la pieza es curva) es un UMBRAL de la calibración:
// `ifc_cobertura_cara_plana` (panel staff). Se recibe por parámetro en `medidasGeometricas`.

/**
 * Medidas de la pieza sacadas de su malla real, en m / m² / m³. Sirven para dos cosas: contrastar las
 * cantidades declaradas (`elegirCantidades`) y ESTIMAR cuando el modelo no declara ninguna.
 *  - área = la CARA GRANDE medida a media pieza: promedio de la cara mayor y su opuesta (muro:
 *    largo×alto, no la huella; losa: la planta; ya descuenta los vanos, porque la malla los tiene).
 *  - longitud = la dimensión mayor, medida orientada con la pieza: en piezas rectas, huella ÷ espesor
 *    en planta o la altura (un muro en diagonal mide su largo); en inclinadas (cabrios, diagonales),
 *    a lo largo del eje mayor de su caja ORIENTADA (`largoOrientado`). Se toma la mayor.
 *  - volumen = volumen real de la malla (si no cierra, huella × alto).
 * Antes se usaba la caja alineada a los ejes del mundo: un muro girado 45° medía mal largo y área.
 */
export function medidasGeometricas(
  acc: AcumuladoGeom,
  factorLongitud: number,
  coberturaCaraPlana: number,
  /** `ifc_forma_lineal_min` de la calibración: habilita el RECORRIDO (`recorridoDeLaPieza`). */
  formaLinealMin?: number,
  /** La clase es un recorrido (IfcRailing): sus tramos paralelos pueden ser ida y vuelta. */
  puedeVolver = false,
): Medidas {
  if (!(acc.areaTotal > 0)) return {};
  const f = factorLongitud;
  let claveMayor = '';
  let areaMayor = 0;
  for (const [k, a] of acc.areaPorNormal) {
    if (a > areaMayor) {
      areaMayor = a;
      claveMayor = k;
    }
  }
  const opuesta = acc.areaPorNormal.get(claveOpuesta(claveMayor)) ?? 0;
  const par = areaMayor + opuesta;
  const cara =
    areaMayor / acc.areaTotal >= coberturaCaraPlana ? (opuesta > 0 ? par / 2 : areaMayor) : acc.areaTotal / 2;

  const casco = cascoConvexo(acc.planta);
  const huella = areaPoligono(casco);
  const rect = rectanguloMinimo(casco);
  const alto = Math.max(0, acc.yMax - acc.yMin);
  const largoEnPlanta = rect.ancho > 0 ? huella / rect.ancho : rect.largo;
  // Largo: el mayor entre el de siempre (altura o largo en planta, exacto en piezas rectas: un muro a
  // dos aguas mide sus 12 m aunque su alzado sea un pentágono) y el de la caja ORIENTADA, que en una
  // pieza inclinada (cabrio, diagonal) es mayor que sus proyecciones y en una recta coincide o queda
  // por debajo. Sin umbral: cada medida gana donde es la correcta. Y en una pieza que en planta no es
  // recta (barandal en L, escalera que sube dando vueltas), su RECORRIDO: las otras tres miden una
  // cuerda (el tramo mayor) y la dejaban hasta 60 % corta.
  const cuerda = Math.max(alto, largoEnPlanta, largoOrientado(acc.puntos) ?? 0);
  const recorrido = formaLinealMin != null ? recorridoDeLaPieza(acc, rect, formaLinealMin, cuerda, puedeVolver) : null;
  const largo = Math.max(cuerda, recorrido ?? 0);

  // Volumen: el de la malla, si es un sólido cerrado. Si no (una superficie suelta), SIN MEDIR: antes se
  // tomaba huella × alto y un barandal de escalera de Revit (sus caras sueltas) salía con 20 a 80 m³.
  const vol = acc.abierta ? 0 : Math.abs(acc.vol6) / 6;

  return {
    area: cara > 0 ? cara * f * f : undefined,
    longitud: largo > 0 ? largo * f : undefined,
    volumen: vol > 0 ? vol * f * f * f : undefined,
  };
}

/**
 * Las medidas de UNA pieza. Una sola regla para las tres magnitudes: lo que el modelo declara y MIDE
 * LO MISMO que la geometría (según las tolerancias de la calibración, `ifc_ventana_*`) se usa tal cual;
 * lo que no declara, o declara pero no cuadra, se MIDE de la malla y se marca como estimado.
 * Ejemplos de FZK-Haus: un cabrio declara su cota, "Länge (A)" y "Breite (B)" (parámetros del objeto)
 * → el largo se mide por su caja orientada; una correa declara 1,04 m² (su cara de arriba) → se mide su
 * cara lateral; una ventana declara 0,04 m³ (sin el vidrio) → se mide el volumen de su malla.
 */
export function medidasDeLaPieza(
  declaradas: readonly CantidadDeclarada[],
  geo: Medidas,
  ventana: VentanaPlausible,
): Medidas & { estimadas: Magnitud[] } {
  const m = declaradas.length ? elegirCantidades(declaradas, geo, ventana) : {};
  const out: Medidas & { estimadas: Magnitud[] } = { estimadas: [] };
  // SIN redondear: la pieza guarda el valor exacto y se redondea solo el TOTAL que se muestra (y que
  // va a la hoja). Redondeada a 3 decimales, una pieza chica (un tubo de NIST, 0,00005 m³) quedaba en 0
  // y la suma del tipo la perdía: los 60 tubos de NIST sumaban 0,000 m³ en vez de 0,003.
  for (const k of ['area', 'longitud', 'volumen'] as const) {
    if (m[k] != null) out[k] = m[k];
    else if (geo[k] != null) {
      out[k] = geo[k];
      out.estimadas.push(k);
    }
  }
  return out;
}

// --- Perfil de eje (IfcCenterLineProfileDef) ------------------------------------------------------------

/** Una malla sin índices compartidos (cada triángulo con sus 3 vértices y su normal plana). */
export interface MallaPlana {
  pos: Float32Array;
  nor: Float32Array;
  idx: Uint32Array;
}

/**
 * La malla de un perfil de EJE extruido (IfcCenterLineProfileDef dentro de un IfcExtrudedAreaSolid): la
 * línea de eje `puntos` (x,y intercalados, en el plano del perfil) con su `espesor` repartido mitad a
 * cada lado —uniones a inglete, como la lámina doblada que describe— y extruida `profundidad` en la
 * dirección `dir` (la ExtrudedDirection). En las coordenadas del sólido; quien la coloca aplica su
 * Position y la colocación de la pieza. web-ifc (0.0.78) no sabe dibujar este perfil y las placas que lo
 * usan quedaban sin malla: sin área, largo, volumen ni peso. Por construcción su volumen es
 * largo del eje × espesor × profundidad. null si el perfil no tiene con qué armarse.
 */
export function mallaDePerfilDeEje(
  puntos: readonly number[],
  espesor: number,
  dir: readonly [number, number, number],
  profundidad: number,
): MallaPlana | null {
  // Puntos sin repetidos consecutivos.
  const p: [number, number][] = [];
  for (let i = 0; i + 1 < puntos.length; i += 2) {
    const q: [number, number] = [puntos[i]!, puntos[i + 1]!];
    const u = p[p.length - 1];
    if (!u || Math.hypot(q[0] - u[0], q[1] - u[1]) > 0) p.push(q);
  }
  const dl = Math.hypot(dir[0], dir[1], dir[2]);
  if (p.length < 2 || !(espesor > 0) || !(profundidad > 0) || !(dl > 0) || dir[2] === 0) return null;
  const n = p.length;
  const h = espesor / 2;
  // Normal de cada tramo y desplazamiento de cada vértice (a inglete en los quiebres).
  const nt: [number, number][] = [];
  for (let i = 0; i + 1 < n; i++) {
    const dx = p[i + 1]![0] - p[i]![0], dy = p[i + 1]![1] - p[i]![1];
    const l = Math.hypot(dx, dy);
    nt.push([-dy / l, dx / l]);
  }
  const izq: [number, number][] = [], der: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const a = nt[Math.max(0, i - 1)]!, b = nt[Math.min(n - 2, i)]!;
    let mx = a[0] + b[0], my = a[1] + b[1];
    const ml = Math.hypot(mx, my);
    let k = h;
    if (ml > 1e-9) {
      mx /= ml;
      my /= ml;
      const c = mx * b[0] + my * b[1];
      if (c > 1e-9) k = h / c;
      else [mx, my] = b;
    } else [mx, my] = b;
    izq.push([p[i]![0] + mx * k, p[i]![1] + my * k]);
    der.push([p[i]![0] - mx * k, p[i]![1] - my * k]);
  }
  // Contorno cerrado y su sentido.
  const contorno = [...izq, ...der.slice().reverse()];
  let a2 = 0;
  for (let i = 0; i < contorno.length; i++) {
    const u = contorno[i]!, v = contorno[(i + 1) % contorno.length]!;
    a2 += u[0] * v[1] - v[0] * u[1];
  }
  if (a2 === 0) return null;
  const antihorario = a2 > 0;
  // Todo se arma en sentido antihorario (tapa de arriba hacia +z); si la extrusión baja, se invierte.
  const invertir = dir[2] < 0;
  const d: [number, number, number] = [(dir[0] / dl) * profundidad, (dir[1] / dl) * profundidad, (dir[2] / dl) * profundidad];
  const tri: number[] = [];
  const agregar = (A: number[], B: number[], C: number[]) => {
    if (invertir) tri.push(...A, ...C, ...B);
    else tri.push(...A, ...B, ...C);
  };
  const abajo = (q: [number, number]) => [q[0], q[1], 0];
  const arriba = (q: [number, number]) => [q[0] + d[0], q[1] + d[1], d[2]];
  // Tapas: un cuadrilátero por tramo (izq_i, izq_i+1, der_i+1, der_i), en el sentido del contorno.
  for (let i = 0; i + 1 < n; i++) {
    const q = [izq[i]!, izq[i + 1]!, der[i + 1]!, der[i]!];
    const [A, B, C, D] = antihorario ? q : [q[0]!, q[3]!, q[2]!, q[1]!];
    agregar(arriba(A!), arriba(B!), arriba(C!));
    agregar(arriba(A!), arriba(C!), arriba(D!));
    agregar(abajo(A!), abajo(C!), abajo(B!));
    agregar(abajo(A!), abajo(D!), abajo(C!));
  }
  // Costados: cada arista del contorno en su sentido, con la normal hacia afuera.
  const anillo = antihorario ? contorno : contorno.slice().reverse();
  for (let i = 0; i < anillo.length; i++) {
    const A = anillo[i]!, B = anillo[(i + 1) % anillo.length]!;
    agregar(abajo(A), abajo(B), arriba(B));
    agregar(abajo(A), arriba(B), arriba(A));
  }
  const pos = new Float32Array(tri);
  const nor = new Float32Array(tri.length);
  for (let i = 0; i < tri.length; i += 9) {
    const ux = tri[i + 3]! - tri[i]!, uy = tri[i + 4]! - tri[i + 1]!, uz = tri[i + 5]! - tri[i + 2]!;
    const vx = tri[i + 6]! - tri[i]!, vy = tri[i + 7]! - tri[i + 1]!, vz = tri[i + 8]! - tri[i + 2]!;
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const l = Math.hypot(nx, ny, nz) || 1;
    nx /= l;
    ny /= l;
    nz /= l;
    for (let k = 0; k < 9; k += 3) {
      nor[i + k] = nx;
      nor[i + k + 1] = ny;
      nor[i + k + 2] = nz;
    }
  }
  const idx = new Uint32Array(tri.length / 3);
  for (let i = 0; i < idx.length; i++) idx[i] = i;
  return { pos, nor, idx };
}

// --- Colocación (matrices 4×4 en orden de columnas, como la flatTransformation de web-ifc) -------------

export type Matriz4 = number[];

export const IDENTIDAD: Matriz4 = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

export function multiplicar(a: readonly number[], b: readonly number[]): Matriz4 {
  const out = new Array<number>(16).fill(0);
  for (let c = 0; c < 4; c++) for (let r = 0; r < 4; r++) {
    let s = 0;
    for (let k = 0; k < 4; k++) s += a[k * 4 + r]! * b[c * 4 + k]!;
    out[c * 4 + r] = s;
  }
  return out;
}

const normalizar = (v: readonly number[]): [number, number, number] => {
  const l = Math.hypot(v[0]!, v[1]!, v[2]!);
  return l > 0 ? [v[0]! / l, v[1]! / l, v[2]! / l] : [0, 0, 0];
};
const cruz = (a: readonly number[], b: readonly number[]): [number, number, number] => [
  a[1]! * b[2]! - a[2]! * b[1]!,
  a[2]! * b[0]! - a[0]! * b[2]!,
  a[0]! * b[1]! - a[1]! * b[0]!,
];

/**
 * IfcAxis2Placement3D → matriz: origen `ubicacion`, z = `eje` (si no, el z global), x = `ref` hecha
 * perpendicular a z (si no, el x global), y = z × x. Es la definición de la norma.
 */
export function matrizDeColocacion(
  ubicacion: readonly number[],
  eje?: readonly number[] | null,
  ref?: readonly number[] | null,
): Matriz4 {
  const z = normalizar(eje && eje.length >= 3 ? eje : [0, 0, 1]);
  let r = ref && ref.length >= 3 ? ref : [1, 0, 0];
  let dz = r[0]! * z[0] + r[1]! * z[1] + r[2]! * z[2];
  let x = normalizar([r[0]! - dz * z[0], r[1]! - dz * z[1], r[2]! - dz * z[2]]);
  if (!(Math.hypot(...x) > 0)) {
    r = Math.abs(z[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
    dz = r[0]! * z[0] + r[1]! * z[1] + r[2]! * z[2];
    x = normalizar([r[0]! - dz * z[0], r[1]! - dz * z[1], r[2]! - dz * z[2]]);
  }
  const y = cruz(z, x);
  return [...x, 0, ...y, 0, ...z, 0, ubicacion[0] ?? 0, ubicacion[1] ?? 0, ubicacion[2] ?? 0, 1];
}

/**
 * IfcCartesianTransformationOperator3D (la MappingTarget de una pieza mapeada) → matriz, con la base de la
 * norma (IfcBaseAxis): u3 = Axis3 (o z), u1 = Axis1 hecho perpendicular a u3 (o x), u2 = Axis2 hecho
 * perpendicular a los dos (o u3 × u1), cada uno por su escala (Scale; Scale2/Scale3 si no es uniforme).
 */
export function matrizDeOperador(
  origen: readonly number[],
  axis1?: readonly number[] | null,
  axis2?: readonly number[] | null,
  axis3?: readonly number[] | null,
  escala = 1,
  escala2 = escala,
  escala3 = escala,
): Matriz4 {
  const u3 = normalizar(axis3 && axis3.length >= 3 ? axis3 : [0, 0, 1]);
  const a1 = axis1 && axis1.length >= 3 ? axis1 : [1, 0, 0];
  const d1 = a1[0]! * u3[0] + a1[1]! * u3[1] + a1[2]! * u3[2];
  const u1 = normalizar([a1[0]! - d1 * u3[0], a1[1]! - d1 * u3[1], a1[2]! - d1 * u3[2]]);
  let u2 = cruz(u3, u1);
  if (axis2 && axis2.length >= 3) {
    const s = axis2[0]! * u2[0] + axis2[1]! * u2[1] + axis2[2]! * u2[2];
    if (s < 0) u2 = [-u2[0], -u2[1], -u2[2]];
  }
  return [
    u1[0] * escala, u1[1] * escala, u1[2] * escala, 0,
    u2[0] * escala2, u2[1] * escala2, u2[2] * escala2, 0,
    u3[0] * escala3, u3[1] * escala3, u3[2] * escala3, 0,
    origen[0] ?? 0, origen[1] ?? 0, origen[2] ?? 0, 1,
  ];
}

/**
 * Del sistema del IFC (z arriba, en la unidad del archivo) al de la geometría de web-ifc (y arriba, en
 * metros): (x, y, z) → (x, z, −y) × factor de longitud. Medido en la flatTransformation que web-ifc
 * entrega (NIST: columnas (0,001;0;0), (0;0;−0,001), (0;0,001;0)).
 */
export function matrizAWebIfc(factorLongitud: number): Matriz4 {
  const f = factorLongitud;
  return [f, 0, 0, 0, 0, 0, -f, 0, 0, f, 0, 0, 0, 0, 0, 1];
}

// --- Volumen por el perfil (para las piezas cuya malla no es un sólido) ----------------------------------

/** Un perfil del IFC ya leído a números (en la unidad del archivo). */
export type PerfilIfc =
  | { tipo: 'rectangulo'; x: number; y: number }
  | { tipo: 'rectanguloHueco'; x: number; y: number; espesor: number }
  | { tipo: 'circulo'; radio: number }
  | { tipo: 'circuloHueco'; radio: number; espesor: number }
  | { tipo: 'I'; ancho: number; alto: number; alma: number; ala: number }
  | { tipo: 'L'; alto: number; ancho: number; espesor: number }
  | { tipo: 'poligono'; exterior: number[]; huecos: number[][] }
  | { tipo: 'eje'; largo: number; espesor: number };

/**
 * Área de un perfil del IFC, con la geometría de la norma (sin filetes: los radios de raíz de un perfil
 * laminado se ignoran, el error es de pocos por ciento). null si no tiene con qué medirse.
 */
export function areaDePerfilIfc(p: PerfilIfc): number | null {
  const pos = (v: number) => (Number.isFinite(v) && v > 0 ? v : null);
  switch (p.tipo) {
    case 'rectangulo':
      return pos(p.x * p.y);
    case 'rectanguloHueco':
      return pos(p.x * p.y - Math.max(0, p.x - 2 * p.espesor) * Math.max(0, p.y - 2 * p.espesor));
    case 'circulo':
      return pos(Math.PI * p.radio * p.radio);
    case 'circuloHueco':
      return pos(Math.PI * (p.radio * p.radio - Math.max(0, p.radio - p.espesor) ** 2));
    case 'I':
      return pos(2 * p.ancho * p.ala + Math.max(0, p.alto - 2 * p.ala) * p.alma);
    case 'L':
      return pos(p.espesor * (p.alto + p.ancho - p.espesor));
    case 'poligono':
      return pos(areaPoligono(p.exterior) - p.huecos.reduce((s, h) => s + areaPoligono(h), 0));
    case 'eje':
      return pos(p.largo * p.espesor);
  }
}

// --- Peso ---------------------------------------------------------------------------------------------

/**
 * Familia de material según la `Category` de IfcMaterial (IFC4). Los valores son los que RECOMIENDA el
 * propio estándar ('concrete', 'steel', 'aluminium', 'wood', 'block', 'brick', 'stone', 'glass'…): es
 * vocabulario de la norma, no una tabla de nombres comerciales. Cualquier otro valor → null.
 */
export function familiaDeCategoria(categoria: string | null | undefined): FamiliaMaterialIfc | null {
  const c = (categoria ?? '').trim().toLowerCase();
  if (c === 'steel') return 'acero';
  if (c === 'concrete') return 'concreto';
  if (c === 'wood') return 'madera';
  if (c === 'aluminium' || c === 'aluminum') return 'aluminio';
  if (c === 'glass') return 'vidrio';
  if (c === 'block' || c === 'brick') return 'mamposteria';
  if (c === 'gypsum') return 'yeso';
  return null;
}

/**
 * Perfiles paramétricos del estándar IFC que describen secciones de acero laminado o doblado (I/H,
 * ángulo, canal, T, C y Z de lámina, tubo): la forma de la sección los delata, no el nombre. Un
 * rectángulo o un contorno libre no dicen nada del material.
 */
export const PERFILES_DE_ACERO = [
  'IFCISHAPEPROFILEDEF',
  'IFCASYMMETRICISHAPEPROFILEDEF',
  'IFCLSHAPEPROFILEDEF',
  'IFCUSHAPEPROFILEDEF',
  'IFCTSHAPEPROFILEDEF',
  'IFCCSHAPEPROFILEDEF',
  'IFCZSHAPEPROFILEDEF',
  'IFCCIRCLEHOLLOWPROFILEDEF',
  'IFCRECTANGLEHOLLOWPROFILEDEF',
] as const;

/** Familia que se deduce de los perfiles (clases IFC) de una pieza: todos de acero → acero; si no, null. */
export function familiaDePerfiles(perfiles: readonly string[]): FamiliaMaterialIfc | null {
  if (!perfiles.length) return null;
  return perfiles.every((p) => (PERFILES_DE_ACERO as readonly string[]).includes(p.toUpperCase())) ? 'acero' : null;
}

/** Con qué densidad se pesa una pieza y de dónde salió. */
export interface DensidadDePieza {
  densidad: number;
  /** `nombre`: la familia la reconoció la IA por el NOMBRE del material (`familiaPorNombreDeMaterial`). */
  origen: 'modelo' | 'familia' | 'nombre';
  familia?: FamiliaMaterialIfc;
  /** La familia no es de la pieza: la heredó de su ensamble (ver `familiaHeredadaDelEnsamble`). */
  heredadaDelEnsamble?: boolean;
  /** Con `origen: 'nombre'`: el material reconocido (nombre tal como lo trae el modelo) y su tipo. */
  deducido?: { material: string; tipo: TipoMaterialIfc };
}

/** Un material de una pieza: su nombre y si el modelo ya dice qué es (categoría, densidad o perfil). */
export interface MaterialDePieza {
  nombre: string | null;
  /** Familia que el modelo ya declara (categoría o perfil); null = no la declara. */
  familia: FamiliaMaterialIfc | null;
  /** El modelo declara su densidad. */
  conDensidad: boolean;
}

/**
 * La LISTA que se le manda a la IA: los nombres de material, DISTINTOS (por `normalizarNombreDeMaterial`),
 * de las piezas que quedaron sin densidad, contando solo los materiales que el modelo NO tipifica (sin
 * familia ni densidad declarada) y que tienen nombre. Cada pieza cuenta una vez por nombre. De más a
 * menos piezas; el nombre que se conserva es el primero que aparece.
 */
export function listaDeMaterialesSinTipo(
  piezasSinDensidad: readonly { eid?: number; materiales: readonly MaterialDePieza[] }[],
): { nombre: string; piezas: number; expressIds?: number[] }[] {
  const por = new Map<string, { nombre: string; piezas: number; orden: number; ids: number[] }>();
  for (const p of piezasSinDensidad) {
    const vistos = new Set<string>();
    for (const m of p.materiales) {
      if (m.familia || m.conDensidad) continue;
      const nombre = (m.nombre ?? '').replace(/\s+/g, ' ').trim();
      const k = normalizarNombreDeMaterial(nombre);
      if (!k || vistos.has(k)) continue;
      vistos.add(k);
      const e = por.get(k);
      if (e) {
        e.piezas++;
        if (p.eid != null) e.ids.push(p.eid);
      } else por.set(k, { nombre, piezas: 1, orden: por.size, ids: p.eid != null ? [p.eid] : [] });
    }
  }
  return [...por.values()]
    .sort((a, b) => b.piezas - a.piezas || a.orden - b.orden)
    .slice(0, MAX_MATERIALES_A_CLASIFICAR)
    // `expressIds`: las piezas de ese material (una por pieza), para «Ver dónde» en el visor. Solo si se dieron.
    .map(({ nombre, piezas, ids }) => ({ nombre, piezas, ...(ids.length ? { expressIds: ids } : {}) }));
}

/**
 * La familia de una pieza con lo que la IA reconoció en el NOMBRE de sus materiales sin tipo: cada
 * material vale por la familia que declara el modelo o, si no declara, por la de su nombre; todas tienen
 * que coincidir (sin el volumen de cada capa no se puede repartir). «otro» / «desconocido» / sin
 * clasificar → null: la pieza no se pesa. `material` es el primer material deducido (para el motivo).
 */
export function familiaPorNombreDeMaterial(
  materiales: readonly MaterialDePieza[],
  tipos: ReadonlyMap<string, TipoMaterialIfc>,
): { familia: FamiliaMaterialIfc; material: string; tipo: TipoMaterialIfc } | null {
  if (!materiales.length) return null;
  let familia: FamiliaMaterialIfc | null = null;
  let deducido: { material: string; tipo: TipoMaterialIfc } | null = null;
  for (const m of materiales) {
    let f = m.familia;
    if (!f) {
      if (m.conDensidad) return null; // densidad sin familia: no se mezcla con una deducida
      const tipo = tipos.get(normalizarNombreDeMaterial(m.nombre ?? ''));
      f = tipo ? familiaDeTipoDeMaterial(tipo) : null;
      if (!f) return null;
      deducido ??= { material: (m.nombre ?? '').replace(/\s+/g, ' ').trim(), tipo: tipo! };
    }
    if (familia && f !== familia) return null;
    familia = f;
  }
  return familia && deducido ? { familia, ...deducido } : null;
}

/**
 * La familia que una pieza SIN familia propia hereda de su IfcElementAssembly: la que dicen los PERFILES
 * de las otras piezas del ensamble (una conexión atornillada de ángulos y vigas de acero: sus placas son
 * acero). Todas las que tienen perfil tienen que coincidir, y ninguna puede declarar por su material una
 * familia distinta (un prefabricado de concreto con placas embebidas no hace concreto a la placa: su
 * familia sale del perfil, nunca del material de otra pieza). Sin perfiles en el ensamble → null.
 * Solo IfcElementAssembly: un muro cortina (IfcCurtainWall) agrega montantes de aluminio y vidrios, y el
 * vidrio no es aluminio.
 */
export function familiaHeredadaDelEnsamble(
  hermanas: readonly { famMat: FamiliaMaterialIfc | null; famPerfil: FamiliaMaterialIfc | null }[],
): FamiliaMaterialIfc | null {
  const dePerfil = new Set(hermanas.map((h) => h.famPerfil).filter((f): f is FamiliaMaterialIfc => f != null));
  if (dePerfil.size !== 1) return null;
  const f = [...dePerfil][0]!;
  return hermanas.every((h) => h.famMat == null || h.famMat === f) ? f : null;
}

/**
 * La densidad de una pieza: la que el MODELO declara para su material (si todos sus materiales la
 * declaran y es la misma: sin el volumen de cada capa no se puede repartir), y si no, la de su FAMILIA
 * (de la calibración). Sin ninguna de las dos → null: la pieza no se pesa, no se inventa. Una densidad
 * declarada en 0 (ArchiCAD la exporta así cuando no se llenó) no es una densidad.
 */
export function densidadDeLaPieza(
  densidadesDelModelo: readonly (number | null)[],
  familia: FamiliaMaterialIfc | null,
  parametros: ParametrosMedicionIfc,
): DensidadDePieza | null {
  const ds = densidadesDelModelo;
  if (ds.length && ds.every((d) => d != null && d > 0)) {
    const d0 = ds[0]!;
    if (ds.every((d) => Math.abs(d! - d0) <= 1e-9 * d0)) return { densidad: d0, origen: 'modelo' };
  }
  if (familia) return { densidad: densidadDeFamiliaIfc(parametros, familia), origen: 'familia', familia };
  return null;
}

/**
 * El PESO (kg) de una pieza. Misma regla que las otras magnitudes: el peso que declara el modelo, si
 * cuadra con volumen × densidad (tolerancia `ifc_ventana_peso`), va tal cual; si no lo declara o no
 * cuadra, volumen × densidad, marcado como estimado. Sin densidad con qué contrastar, el declarado se
 * usa como viene (el neto si hay); sin declarado ni densidad, sin peso.
 */
export function pesoDeLaPieza(
  declaradas: readonly CantidadDeclarada[],
  volumen: number | undefined,
  densidad: DensidadDePieza | null,
  ventanaPeso: number,
): { peso?: number; estimado: boolean } {
  const cands = declaradas.filter((q) => q.magnitud === 'peso' && Number.isFinite(q.valor) && q.valor > 0);
  const ref = volumen != null && volumen > 0 && densidad ? volumen * densidad.densidad : null;
  if (cands.length) {
    if (ref == null) return { peso: (cands.find((q) => esNeta(q.nombre)) ?? cands[0])!.valor, estimado: false };
    const lejania = (v: number) => Math.abs(Math.log(v / ref));
    const plaus = cands.filter((q) => lejania(q.valor) <= Math.log(ventanaPeso));
    if (plaus.length) {
      const mejor = plaus.reduce((a, b) => {
        const da = lejania(a.valor);
        const db = lejania(b.valor);
        if (db < da - 1e-12) return b;
        if (Math.abs(db - da) <= 1e-12 && esNeta(b.nombre) && !esNeta(a.nombre)) return b;
        return a;
      });
      return { peso: mejor.valor, estimado: false };
    }
  }
  // Exacto (sin redondear), como las otras magnitudes de la pieza: se redondea el total del tipo.
  return ref != null ? { peso: ref, estimado: true } : { estimado: false };
}

// --- La medida que manda ----------------------------------------------------------------------------

/** Forma de una pieza: por qué magnitud se mide. */
export type FormaIfc = 'longitud' | 'area' | 'volumen';

/**
 * Las tres dimensiones de la caja ORIENTADA de la pieza (sus ejes principales, `ejesPrincipales`), de la
 * mayor a la menor, en las unidades de los puntos. Es la forma "de afuera": una viga en I da largo ×
 * peralte × patín aunque sus placas sean delgadas.
 */
export function dimensionesOrientadas(pts: readonly number[]): [number, number, number] | null {
  if (pts.length < 6) return null;
  const ejes = ejesPrincipales(pts);
  const d = ejes.map((e) => {
    let mn = Infinity, mx = -Infinity;
    for (let i = 0; i < pts.length; i += 3) {
      const v = pts[i]! * e[0] + pts[i + 1]! * e[1] + pts[i + 2]! * e[2];
      if (v < mn) mn = v;
      if (v > mx) mx = v;
    }
    return mx - mn;
  });
  d.sort((x, y) => y - x);
  return [d[0]!, d[1]!, d[2]!];
}

/**
 * La FORMA de una pieza por las dimensiones de su caja orientada (a ≥ b ≥ c): PLANA si b es al menos
 * `ifc_forma_plana_min` veces c (muro, losa, vidrio: su sección es una lámina); si no, LINEAL si a es al
 * menos `ifc_forma_lineal_min` veces b (columna, viga, tubo: su sección es compacta); si no, volumétrica
 * (zapata, dado, relleno). Plana se mira primero: un muro largo y bajo es alargado, pero es una lámina.
 * Los dos umbrales son de la calibración. Sin dimensiones, no se sabe (null).
 */
export function formaDePieza(
  dims: readonly [number, number, number] | null | undefined,
  p: Pick<ParametrosMedicionIfc, 'ifc_forma_lineal_min' | 'ifc_forma_plana_min'>,
): FormaIfc | null {
  if (!dims) return null;
  const [a, b, c] = dims;
  if (!(a > 0) || !(b > 0)) return null;
  if (!(c > 0) || b / c >= p.ifc_forma_plana_min) return 'area';
  return a / b >= p.ifc_forma_lineal_min ? 'longitud' : 'volumen';
}

/**
 * La magnitud por la que se presupuesta un TIPO (la "medida que manda"), por voto de sus piezas, sin
 * ninguna tabla por clase:
 *  - lo que rellena un vano (IfcRelFillsElement del modelo: puertas, ventanas) → por pieza;
 *  - si no, PRIMERO lo que el modelo declara: si sus cantidades (Qto) traen una sola de las tres
 *    magnitudes (una cubierta de Revit declara solo áreas), esa; si traen varias, decide la forma;
 *  - si no, lo que el ESTÁNDAR declara para su clase (`magnitudesDelEstandar`): si su
 *    Qto_*BaseQuantities trae una sola magnitud (IfcRailing: largo; IfcRoof: áreas), esa — salvo en
 *    piezas con puertos, que son equipos (el Qto de una caldera es su superficie de intercambio);
 *  - si no, la forma que tiene la mayoría de sus piezas (`formaDePieza`);
 *  - una forma volumétrica en piezas con conexiones de instalación (puertos: codos, llaves, muebles
 *    sanitarios, equipos) → por pieza: un codo no se cotiza por m³;
 *  - sin geometría con qué decidir → por pieza.
 */
export function medidaQueManda(
  piezas: readonly { forma: FormaIfc | null; rellenaVano: boolean; conPuertos: boolean; declaradas?: readonly FormaIfc[] }[],
  delEstandar: readonly FormaIfc[] = [],
): 'area' | 'longitud' | 'volumen' | 'conteo' {
  const estandar = delEstandar.length === 1 ? delEstandar[0]! : null;
  if (!piezas.length) return 'conteo';
  const mayoria = (f: (x: (typeof piezas)[number]) => boolean) => piezas.filter(f).length * 2 > piezas.length;
  if (mayoria((x) => x.rellenaVano)) return 'conteo';
  const votos = new Map<FormaIfc, number>();
  for (const x of piezas) {
    const d = [...new Set(x.declaradas ?? [])];
    const f = d.length === 1 ? d[0]! : (!x.conPuertos && estandar) || x.forma;
    if (f) votos.set(f, (votos.get(f) ?? 0) + 1);
  }
  let gana: FormaIfc | null = null;
  for (const f of ['longitud', 'area', 'volumen'] as const) if ((votos.get(f) ?? 0) > (gana ? votos.get(gana)! : 0)) gana = f;
  if (!gana) return 'conteo';
  if (gana === 'volumen' && mayoria((x) => x.conPuertos)) return 'conteo';
  return gana;
}

/**
 * Las magnitudes que el estándar IFC declara para medir una clase (su Qto_*BaseQuantities, de las
 * plantillas oficiales: `MAGNITUDES_QTO_DEL_ESTANDAR`, generado). Si la clase no tiene Qto propio, el
 * de su supertipo más cercano que lo tenga (IfcReinforcingBar → IfcReinforcingElement). [] si ninguno.
 */
export function magnitudesDelEstandar(clase: string | null | undefined, supertipos: readonly string[] = []): readonly FormaIfc[] {
  for (const c of [clase, ...supertipos]) {
    const m = c ? MAGNITUDES_QTO_DEL_ESTANDAR[c] : undefined;
    if (m) return m;
  }
  return [];
}

/**
 * Suma las instancias de una capa (lo que va a la hoja). Las piezas traen su valor EXACTO; se redondea
 * solo este total (m y m² a 2 decimales, m³ a 3, kg a 2), que es lo que se muestra.
 */
export function sumarCapa(insts: readonly Instancia[], nEntidades: number): NonNullable<Capa['medidas']> {
  let area = 0;
  let longitud = 0;
  let volumen = 0;
  let peso = 0;
  let algunaEstim = false;
  const estimadas = new Set<MagnitudEstimable>();
  for (const i of insts) {
    if (i.area) area += i.area;
    if (i.longitud) longitud += i.longitud;
    if (i.volumen) volumen += i.volumen;
    if (i.peso) peso += i.peso;
    if (i.estimadoGeometria) algunaEstim = true;
    for (const m of i.magnitudesEstimadas ?? []) estimadas.add(m);
  }
  const out: NonNullable<Capa['medidas']> = {
    conteo: insts.length || nEntidades,
    area: area > 0 ? r2(area) : undefined,
    longitud: longitud > 0 ? r2(longitud) : undefined,
    volumen: volumen > 0 ? r3(volumen) : undefined,
    estimadoGeometria: algunaEstim,
  };
  if (peso > 0) out.peso = r2(peso);
  if (estimadas.size) out.magnitudesEstimadas = (['area', 'longitud', 'volumen', 'peso'] as const).filter((m) => estimadas.has(m));
  const origenes = new Set(insts.map((i) => i.origenVolumen).filter((o): o is NonNullable<typeof o> => o != null));
  if (origenes.size) out.origenesVolumen = (['declarado', 'malla', 'perfil'] as const).filter((o) => origenes.has(o));
  const sinVol = insts.filter((i) => i.sinVolumen);
  if (sinVol.length) out.sinVolumen = { piezas: sinVol.length, motivo: sinVol[0]!.sinVolumen! };
  const deducidos = new Map<string, { material: string; tipo: TipoMaterialIfc; piezas: number }>();
  for (const i of insts) {
    if (!i.pesoDeducido) continue;
    const k = `${normalizarNombreDeMaterial(i.pesoDeducido.material)}|${i.pesoDeducido.tipo}`;
    const e = deducidos.get(k);
    if (e) e.piezas++;
    else deducidos.set(k, { ...i.pesoDeducido, piezas: 1 });
  }
  if (deducidos.size) out.pesosDeducidos = [...deducidos.values()].sort((a, b) => b.piezas - a.piezas);
  return out;
}

// --- Niveles deducidos (modelo sin pisos) ---------------------------------------------------------------

/**
 * Las cotas de los niveles de un modelo que no trae pisos, a partir de la cara superior de sus losas de
 * piso: se ordenan y se juntan las que quedan a menos de `tolerancia` de la PRIMERA del grupo (no de la
 * anterior, para que una rampa de losas escalonadas no encadene todo en un solo nivel). La cota del
 * nivel es la más baja de su grupo. De abajo hacia arriba.
 */
export function cotasDeNiveles(cotas: readonly number[], tolerancia: number): number[] {
  const orden = cotas.filter((c) => Number.isFinite(c)).sort((a, b) => a - b);
  const out: number[] = [];
  for (const c of orden) if (!out.length || c - out[out.length - 1]! > tolerancia) out.push(c);
  return out;
}

/**
 * El nivel (índice en `cotas`, ascendentes) de una pieza cuya base está a `base`: el más alto cuya cota
 * queda por debajo de su base, admitiendo que arranque hasta `tolerancia` más abajo (un muro que nace
 * en el lecho bajo de la losa). Lo que queda debajo del primer nivel (cimientos) es del primero.
 */
export function nivelPorBase(base: number, cotas: readonly number[], tolerancia: number): number {
  let k = 0;
  for (let i = 0; i < cotas.length; i++) if (cotas[i]! <= base + tolerancia) k = i;
  return k;
}

// =====================================================================================================
// 2. ADAPTADOR web-ifc
// =====================================================================================================

/**
 * Nivel del modelo para "Ver por nivel": un piso del edificio (IfcBuildingStorey) o, en infraestructura
 * IFC 4.3, una PARTE de la instalación (IfcFacilityPart: IfcBridgePart, IfcRoadPart, IfcRailwayPart…),
 * que es como esos modelos se dividen. Una parte no tiene altura de piso: se filtra, no se corta.
 */
export interface NivelIfc {
  id: number;
  nombre: string;
  /** Sin dato = piso. */
  tipo?: 'piso' | 'parte';
  /**
   * Altura del piso en las MISMAS coordenadas que la geometría (Y, metros): la de su colocación real
   * (cadena de IfcLocalPlacement), que incluye la cota del terreno; si no se puede leer, el atributo
   * Elevation. null si no hay ninguna.
   */
  elevacion: number | null;
  /** Cuántos elementos cuelgan de este nivel (los vacíos no se ofrecen en "Ver por nivel"). */
  elementos: number;
  /**
   * El modelo no trae pisos y este nivel se DEDUJO de la cota de sus losas de piso (`cotasDeNiveles`).
   * Su id es negativo (no es una línea del archivo).
   */
  deducido?: boolean;
}

/**
 * web-ifc (0.0.57 y 0.0.78, medido) entrega la geometría colocada (posición × flatTransformation) en metros, sea cual sea
 * la unidad del archivo. Lo usa el visor para NO volver a escalar el modelo.
 */
export const GEOMETRIA_WEBIFC_EN_METROS = true;

/** Una OBRA del modelo (IfcFacility de IFC 4.3 o IfcBuilding): un puente, una carretera, un edificio. */
export interface ObraIfc {
  id: number;
  /** Como la nombra el modelo (Name; si no, LongName). */
  nombre: string;
  /** Clase IFC (IfcBridge, IfcRoad, IfcBuilding…). */
  clase: string;
}

export interface ResultadoMedicion {
  capas: Capa[];
  /** expressID → etiqueta de la capa (tipo) a la que pertenece. */
  expressAEtiqueta: Map<number, string>;
  factores: FactoresUnidad;
  /** Niveles ordenados por elevación (los sin elevación al final, en el orden del modelo). */
  niveles: NivelIfc[];
  /** expressID de cualquier elemento → id de su nivel (por contención, espacio o pieza de un conjunto). */
  nivelDeElemento: Map<number, number>;
  /** Obras del modelo y de cuál es cada pieza ("Agrupar por obra" cuando hay más de una). */
  obras: ObraIfc[];
  obraDeElemento: Map<number, number>;
  /** Diagnóstico: lo que mide la malla de cada elemento (la referencia contra la que se eligió). */
  geometria: Map<number, Medidas>;
  /**
   * Elementos que son TECHO según el propio estándar IFC: IfcRoof y todo lo que agrupa, las losas con
   * PredefinedType ROOF (en el elemento o en su tipo) y los recubrimientos ROOFING. Para "Ocultar techo".
   */
  elementosDeTecho: Set<number>;
  /** Con qué densidad se pesó cada pieza y de dónde salió (las que no tienen entrada, no se pesaron). */
  densidad: Map<number, DensidadDePieza>;
  /**
   * Piezas con geometría que NO encierran volumen (`esSolidoCerrado`): su volumen y su peso quedan sin
   * medir. Para decírselo a la persona: `MOTIVO_SIN_SOLIDO`.
   */
  sinSolido: Set<number>;
  /** Clase (canónica) de cada pieza inventariada: para el color de respaldo del visor. */
  claseDe: Map<number, string>;
  /** Esquema del archivo, instalaciones (IfcFacility) que trae y el aviso para la persona, si hay. */
  ifc: NonNullable<PlanoInventario['ifc']>;
}

// --- Esquema y herencia -------------------------------------------------------------------------------

/**
 * El esquema del archivo contra los que conoce web-ifc (`SchemaNames`: cada fila es un esquema final y
 * sus alias). Un alias que no es el esquema final (IFC4X3_RC2, IFC4X2, IFC4X1…) es una versión
 * PRELIMINAR del formato: web-ifc lo lee con el esquema final más cercano "a lo que se pueda" y puede
 * perder o leer mal atributos. La única excepción es IFC4X3_ADD2, que es la norma publicada (web-ifc la
 * trata igual). Devuelve el índice del esquema (para `InheritanceDef`) y si es preliminar.
 */
export function esquemaDelArchivo(
  nombre: string,
  schemaNames: readonly (readonly string[] | undefined)[],
): { indice: number; canonico: string; preliminar: boolean } {
  const n = nombre.trim().toUpperCase();
  const indice = schemaNames.findIndex((fila) => fila?.includes(n));
  if (indice < 0) return { indice: -1, canonico: n, preliminar: false };
  const canonico = schemaNames[indice]![0]!;
  return { indice, canonico, preliminar: n !== canonico && n !== 'IFC4X3_ADD2' };
}

/** El aviso que se le da a la persona cuando el esquema es preliminar. */
export function avisoDeEsquema(nombre: string, canonico: string): string {
  return (
    `Este IFC usa una versión preliminar del formato (${nombre}). Se leyó como ${canonico}, la versión final ` +
    'más cercana, y puede faltar o leerse mal alguna pieza. Si puedes, vuelve a exportarlo en una versión final.'
  );
}

/**
 * La clase con la que se AGRUPA una pieza (las subclases «…StandardCase» / «…ElementedCase» se agrupan con su
 * clase madre). La regla vive en `@supernova/shared` porque la memoria de tipos la necesita igual; aquí se
 * re-exporta para no cambiar a quien ya la importa desde este archivo.
 */
export { claseCanonica };

type OrigenDeTipo = 'tipo' | 'objectType' | 'nombre' | 'clase';
/** De más a menos fuerte: el origen de un grupo de piezas es el más débil de las suyas. */
const ORDEN_DE_ORIGEN: readonly OrigenDeTipo[] = ['tipo', 'objectType', 'nombre', 'clase'];

/**
 * Los CONTENEDORES que no se cuentan: un elemento que agrupa partes (IfcRelAggregates) que también están
 * en el inventario —un IfcRoof con sus losas, un IfcCurtainWall con sus paneles y montantes, un IfcStair
 * con sus tramos, un muro de Vectorworks con sus capas (IfcBuildingElementPart)— se cuenta en sus partes.
 * Contarlo además duplicaba el techo y medía el muro cortina dos veces. La norma lo dice así: un elemento
 * descompuesto se representa por la suma de sus partes, y si trae un cuerpo propio es esa misma suma.
 * Decide QUIÉN TRAE EL CUERPO (`tieneCuerpo`: una representación 'Body'), no la clase:
 *  - alguna parte trae cuerpo → se cuentan las partes (las piezas reales, cada una con su material);
 *  - ninguna parte lo trae y el contenedor sí (un exportador que puso el sólido en el contenedor y dejó
 *    las partes como datos) → se cuenta el contenedor: si no, la obra quedaba sin medir;
 *  - si ninguna de sus partes está en el inventario, el contenedor se cuenta (es lo único que hay).
 * Sin `tieneCuerpo`, las partes mandan siempre.
 */
export function contenedoresConPartes(
  padreDe: ReadonlyMap<number, number>,
  inventariables: ReadonlySet<number>,
  tieneCuerpo?: (id: number) => boolean,
): Set<number> {
  const partes = new Map<number, number[]>();
  for (const [hijo, padre] of padreDe) {
    if (!inventariables.has(hijo) || !inventariables.has(padre)) continue;
    (partes.get(padre) ?? partes.set(padre, []).get(padre)!).push(hijo);
  }
  const out = new Set<number>();
  for (const [padre, hijos] of partes) {
    if (tieneCuerpo && tieneCuerpo(padre) && !hijos.some(tieneCuerpo)) continue;
    out.add(padre);
  }
  return out;
}

const enumVal = (x: any): string => (typeof x === 'string' ? x : (x?.value ?? '')).toString().toUpperCase();
const num = (x: any): number | undefined => (typeof x?.value === 'number' ? x.value : undefined);

/**
 * Crea el medidor de un modelo abierto. Uso (visor y banco de prueba):
 *   const m = crearMedidor(api, WebIFC, modelID, parametrosDeLaCalibracion);
 *   const etiquetaDe = await m.inventariar();
 *   api.StreamAllMeshes(modelID, (flat) => … m.acumular(flat.expressID, pos, idx, pg.flatTransformation));
 *   const r = m.cerrar();
 */
export function crearMedidor(
  api: any,
  WebIFC: any,
  modelID: number,
  parametros: ParametrosMedicionIfc = PARAMETROS_MEDICION_IFC_DEFAULT,
  /** Avance del inventario (0–1), para que la pantalla diga cuánto falta. */
  onAvance?: (fraccion: number) => void,
) {
  const ventana = ventanaDe(parametros);
  /** Las líneas de una clase; con `heredadas`, también las de todas sus subclases (según el esquema). */
  const idsDe = (code: number | undefined, heredadas = false): number[] => {
    const out: number[] = [];
    if (code == null) return out;
    try {
      const v = api.GetLineIDsWithType(modelID, code, heredadas);
      for (let i = 0; i < v.size(); i++) out.push(v.get(i));
    } catch {
      /* clase ausente en el esquema del modelo */
    }
    return out;
  };

  // --- Esquema: nombre, si es preliminar, y la herencia de clases (InheritanceDef de web-ifc) ----------
  const nombreEsquema = (() => {
    try {
      return String(api.GetModelSchema(modelID) ?? '');
    } catch {
      return '';
    }
  })();
  const esquema = esquemaDelArchivo(nombreEsquema, (WebIFC.SchemaNames ?? []) as string[][]);
  const herencia: Record<string, number[]> = (esquema.indice >= 0 && WebIFC.InheritanceDef?.[esquema.indice]) || {};
  const nombreDeClase = (code: number): string => {
    try {
      return String(api.GetNameFromTypeCode(code) ?? '') || `#${code}`;
    } catch {
      return `#${code}`;
    }
  };
  const supertiposCache = new Map<number, string[]>();
  /** Ancestros de una clase, del más cercano (menos descendientes) al más lejano. */
  const supertiposDe = (code: number): string[] => {
    let s = supertiposCache.get(code);
    if (s) return s;
    const anc: { code: number; n: number }[] = [];
    for (const [k, desc] of Object.entries(herencia)) if (Array.isArray(desc) && desc.includes(code)) anc.push({ code: Number(k), n: desc.length });
    anc.sort((a, b) => a.n - b.n);
    s = anc.map((a) => nombreDeClase(a.code));
    supertiposCache.set(code, s);
    return s;
  };
  const esDe = (code: number, ancestro: number | undefined): boolean =>
    ancestro != null && (code === ancestro || (herencia[String(ancestro)] ?? []).includes(code));
  const tipoDeLinea = (id: number): number | undefined => {
    try {
      return api.GetLineType(modelID, id);
    } catch {
      return undefined;
    }
  };
  const linea = (id: number | null | undefined): any => {
    // Sin referencia no se le pregunta a web-ifc: lo anota como error en la consola ("Invalid IFC Line").
    if (id == null) return null;
    try {
      return api.GetLine(modelID, id);
    } catch {
      return null;
    }
  };

  // --- Unidades: IfcProject.UnitsInContext → cada IfcSIUnit / IfcConversionBasedUnit --------------------
  const leerUnidad = (unit: any, prof = 0): UnidadIfc | null => {
    const tipo = enumVal(unit?.UnitType);
    if (!tipo || prof > 3) return null;
    if (unit.ConversionFactor?.value != null) {
      const cf = linea(unit.ConversionFactor.value);
      const valor = cf?.ValueComponent?.value;
      if (!(typeof valor === 'number' && valor > 0)) return null;
      const uc = linea(cf?.UnitComponent?.value);
      const pb = enumVal(uc?.Prefix);
      return { tipo, conversion: { valor, prefijoBase: pb || undefined } };
    }
    if (Array.isArray(unit.Elements)) {
      // IfcDerivedUnit (kg/m³): cada IfcDerivedUnitElement trae su unidad y su exponente.
      const elementos: NonNullable<UnidadIfc['elementos']> = [];
      for (const eref of unit.Elements) {
        const el = linea(eref?.value);
        const u = leerUnidad(linea(el?.Unit?.value), prof + 1);
        const exponente = typeof el?.Exponent === 'number' ? el.Exponent : el?.Exponent?.value;
        if (u && typeof exponente === 'number') elementos.push({ unidad: u, exponente });
      }
      return { tipo, elementos };
    }
    const p = enumVal(unit.Prefix);
    return { tipo, prefijo: p || undefined };
  };
  const leerUnidades = (): UnidadIfc[] => {
    const out: UnidadIfc[] = [];
    for (const pid of idsDe(WebIFC.IFCPROJECT)) {
      const ua = linea(linea(pid)?.UnitsInContext?.value);
      for (const uref of ua?.Units ?? []) {
        const u = uref?.value != null ? leerUnidad(linea(uref.value)) : null;
        if (u) out.push(u);
      }
      if (out.length) break;
    }
    return out;
  };
  const factores = factoresDeUnidades(leerUnidades());
  if (!factores.longitudDeclarada) {
    console.warn('[medicionIfc] El IFC no declara unidad de longitud; se asume metro.');
  } else if (factores.longitud !== 1 || factores.area !== 1 || factores.volumen !== 1 || factores.masa !== 1 || factores.densidad !== 1) {
    console.info('[medicionIfc] Unidades del modelo → metros:', factores);
  }

  const capas: Capa[] = [];
  const expressAEtiqueta = new Map<number, string>();
  const cantidadesDe = new Map<number, CantidadDeclarada[]>();
  const geomDe = new Map<number, AcumuladoGeom>();
  const niveles: NivelIfc[] = [];
  const nivelDeElemento = new Map<number, number>();
  const elementosDeTecho = new Set<number>();
  const densidadDe = new Map<number, DensidadDePieza>();
  /** Piezas que quedaron sin densidad y sus materiales (para reconocerlos por su nombre). */
  const sinDensidad: { eid: number; materiales: MaterialDePieza[] }[] = [];
  /** Lo que se reconoció por nombre (`aplicarTiposDeMaterial`), por nombre normalizado. */
  let materialesReconocidos: { nombre: string; piezas: number; expressIds?: number[]; tipo: TipoMaterialIfc }[] = [];
  const claseDe = new Map<number, string>();
  const instalaciones: string[] = [];
  const rellenaVano = new Set<number>();
  /** Números generadores: las puertas y ventanas que llenan los vanos de cada pieza (muro → vano → la que lo llena). */
  const alojadosDe = new Map<number, number[]>();
  const conPuertos = new Set<number>();
  /** Las OBRAS del modelo (IfcFacility: IfcBuilding, IfcBridge, IfcRoad, IfcRailway…) y de cuál es cada pieza. */
  const obras: ObraIfc[] = [];
  const obraDeElemento = new Map<number, number>();
  /** Losas de piso según el estándar (IfcSlab FLOOR/BASESLAB): de su cota salen los niveles deducidos. */
  const losasDePiso = new Set<number>();

  /**
   * Cantidades físicas (IfcElementQuantity) de TODOS los elementos, ya en m/m²/m³/kg, según el campo
   * TIPADO, en UNA pasada por IfcRelDefinesByProperties. Antes se pedían pieza por pieza con
   * `properties.getPropertySets`, que recorre todas las relaciones del modelo en cada llamada: con 20 mil
   * piezas eso era lo que congelaba la carga.
   */
  let cantidadesPorElemento: Map<number, CantidadDeclarada[]> | null = null;
  const indexarCantidades = (): Map<number, CantidadDeclarada[]> => {
    const out = new Map<number, CantidadDeclarada[]>();
    const deQto = new Map<number, CantidadDeclarada[]>();
    const leerQto = (id: number): CantidadDeclarada[] => {
      const hecho = deQto.get(id);
      if (hecho) return hecho;
      const lista: CantidadDeclarada[] = [];
      const ps = linea(id);
      // Solo cantidades tipadas (Quantities); las propiedades descriptivas no son medidas de obra.
      for (const qref of Array.isArray(ps?.Quantities) ? ps.Quantities : []) {
        const q = linea(qref?.value);
        const nombre = (q?.Name?.value ?? '').toString();
        const a = num(q?.AreaValue);
        const l = num(q?.LengthValue);
        const v = num(q?.VolumeValue);
        const w = num(q?.WeightValue);
        if (w != null) lista.push({ nombre, magnitud: 'peso', valor: w * factores.masa });
        else if (a != null) lista.push({ nombre, magnitud: 'area', valor: a * factores.area });
        else if (v != null) lista.push({ nombre, magnitud: 'volumen', valor: v * factores.volumen });
        else if (l != null) lista.push({ nombre, magnitud: 'longitud', valor: l * factores.longitud });
      }
      deQto.set(id, lista);
      return lista;
    };
    for (const rid of idsDe(WebIFC.IFCRELDEFINESBYPROPERTIES)) {
      const rel = linea(rid);
      const def = rel?.RelatingPropertyDefinition?.value;
      if (def == null || tipoDeLinea(def) !== WebIFC.IFCELEMENTQUANTITY) continue;
      const qs = leerQto(def);
      if (!qs.length) continue;
      for (const o of rel?.RelatedObjects ?? []) {
        if (o?.value == null) continue;
        const prev = out.get(o.value);
        out.set(o.value, prev ? prev.concat(qs) : qs);
      }
    }
    return out;
  };
  const leerCantidades = (eid: number): CantidadDeclarada[] =>
    (cantidadesPorElemento ??= indexarCantidades()).get(eid) ?? [];

  /** Arma el inventario por tipo (sin medidas todavía). Devuelve expressID → etiqueta de su capa. */
  async function inventariar(): Promise<Map<number, string>> {
    // 1) elemento → nombre de su TIPO (IfcRelDefinesByType).
    const elemAType = new Map<number, string>();
    const elemATipoId = new Map<number, number>();
    for (const rid of idsDe(WebIFC.IFCRELDEFINESBYTYPE)) {
      const rel = linea(rid);
      const tipoId = rel?.RelatingType?.value;
      for (const obj of rel?.RelatedObjects ?? []) if (obj?.value != null && tipoId != null) elemATipoId.set(obj.value, tipoId);
      const tn = tipoDesdeInstancia(linea(tipoId)?.Name?.value ?? '');
      if (!tn) continue;
      for (const obj of rel?.RelatedObjects ?? []) if (obj?.value != null) elemAType.set(obj.value, tn);
    }

    // 2) Ubicación espacial: nivel (piso) y zona (espacio). Sale del modelo (contención), no se infiere.
    // El PISO trae su nombre en Name ("Nivel 1", "Erdgeschoss"); ArchiCAD pone en su LongName un id
    // interno ("ACID00000001-…"). El ESPACIO, al revés: Name = número ("101"), LongName = "Recámara".
    const nombreEspacial = (l: any, preferirLargo: boolean): string => {
      const nm = normNombre((l?.Name?.value ?? '').toString());
      const lng = normNombre((l?.LongName?.value ?? '').toString());
      return preferirLargo ? lng || nm : nm || lng;
    };
    const pisoNombre = new Map<number, string>();
    const zonaNombre = new Map<number, string>();
    // Altura real del piso: suma de las Z de su cadena de colocaciones (IfcLocalPlacement → PlacementRelTo),
    // en las mismas coordenadas que la geometría. El atributo Elevation no incluye la cota del terreno.
    const zDeColocacion = (pid: number | undefined, prof = 0): number | null => {
      if (pid == null || prof > 20) return prof === 0 ? null : 0;
      const lp = linea(pid);
      if (!lp) return null;
      const coords = linea(linea(lp.RelativePlacement?.value)?.Location?.value)?.Coordinates;
      const z = typeof coords?.[2]?.value === 'number' ? coords[2].value : 0;
      const arriba = lp.PlacementRelTo?.value != null ? zDeColocacion(lp.PlacementRelTo.value, prof + 1) : 0;
      return arriba == null ? null : z + arriba;
    };
    for (const id of idsDe(WebIFC.IFCBUILDINGSTOREY)) {
      const l = linea(id);
      const nm = nombreEspacial(l, false);
      if (nm) pisoNombre.set(id, nm);
      const zc = zDeColocacion(l?.ObjectPlacement?.value);
      const elev = zc ?? num(l?.Elevation);
      niveles.push({
        id,
        nombre: nm || `Nivel ${niveles.length + 1}`,
        tipo: 'piso',
        elevacion: elev != null ? elev * factores.longitud : null,
        elementos: 0,
      });
    }
    // Infraestructura (IFC 4.3): las PARTES de la instalación (IfcBridgePart, IfcRoadPart…) son como se
    // divide el modelo. Sin altura de piso: se filtran, no se cortan. Una parte que solo agrupa otras
    // partes queda sin elementos propios y no se ofrece (las piezas cuelgan de la parte más cercana).
    for (const id of idsDe(WebIFC.IFCFACILITYPART, true)) {
      const l = linea(id);
      const nm = nombreEspacial(l, true);
      if (nm) pisoNombre.set(id, nm);
      niveles.push({ id, nombre: nm || `Parte ${niveles.length + 1}`, tipo: 'parte', elevacion: null, elementos: 0 });
    }
    // Las instalaciones (IfcFacility de IFC 4.3; en IFC2x3/IFC4 solo existe IfcBuilding).
    const instalacionDeId = new Map<number, string>();
    for (const id of [...idsDe(WebIFC.IFCFACILITY, true), ...idsDe(WebIFC.IFCBUILDING)]) {
      const t = tipoDeLinea(id);
      if (t == null || instalacionDeId.has(id)) continue;
      instalacionDeId.set(id, nombreDeClase(t));
      obras.push({ id, nombre: nombreEspacial(linea(id), false) || `Obra ${obras.length + 1}`, clase: nombreDeClase(t) });
    }
    for (const id of idsDe(WebIFC.IFCSPACE)) {
      const nm = nombreEspacial(linea(id), true);
      if (nm) zonaNombre.set(id, nm);
    }
    niveles.sort((a, b) => (a.elevacion ?? Infinity) - (b.elevacion ?? Infinity));
    const elemAPiso = new Map<number, string>();
    const elemAZona = new Map<number, string>();
    const contenidoEn = new Map<number, number>(); // elemento → estructura espacial que lo contiene
    for (const code of [WebIFC.IFCRELCONTAINEDINSPATIALSTRUCTURE, WebIFC.IFCRELREFERENCEDINSPATIALSTRUCTURE]) {
      for (const rid of idsDe(code)) {
        const rel = linea(rid);
        const structID = rel?.RelatingStructure?.value;
        if (structID == null) continue;
        const piso = pisoNombre.get(structID);
        const zona = zonaNombre.get(structID);
        for (const obj of rel?.RelatedElements ?? rel?.RelatedObjects ?? []) {
          const eid = obj?.value;
          if (eid == null) continue;
          if (code === WebIFC.IFCRELCONTAINEDINSPATIALSTRUCTURE && !contenidoEn.has(eid)) contenidoEn.set(eid, structID);
          if (piso && !elemAPiso.has(eid)) elemAPiso.set(eid, piso);
          if (zona && !elemAZona.has(eid)) elemAZona.set(eid, zona);
        }
      }
    }
    // Nivel de CUALQUIER elemento: el piso que lo contiene; si lo contiene un espacio, el piso de ese
    // espacio; si es pieza de un conjunto (IfcRelAggregates: barandal, escalera), el de su conjunto.
    const padreDe = new Map<number, number>();
    for (const rid of idsDe(WebIFC.IFCRELAGGREGATES)) {
      const rel = linea(rid);
      const p = rel?.RelatingObject?.value;
      if (p == null) continue;
      for (const o of rel?.RelatedObjects ?? []) if (o?.value != null) padreDe.set(o.value, p);
    }
    // Partes con el MISMO nombre (cada tramo de una carretera tiene su "carriageway" y su "shoulder") son
    // la misma sección del modelo: se ofrecen una vez, con las piezas de todas.
    const representante = new Map<number, number>();
    const primeraParte = new Map<string, number>();
    for (const n of niveles) {
      if (n.tipo !== 'parte') continue;
      const r = primeraParte.get(n.nombre);
      if (r == null) primeraParte.set(n.nombre, n.id);
      else representante.set(n.id, r);
    }
    if (representante.size) niveles.splice(0, niveles.length, ...niveles.filter((n) => !representante.has(n.id)));
    const esNivel = new Set([...niveles.map((n) => n.id), ...representante.keys()]);
    const nivelDe = (eid: number, prof = 0): number | undefined => {
      if (esNivel.has(eid)) return representante.get(eid) ?? eid;
      if (prof > 8) return undefined;
      const arriba = contenidoEn.get(eid) ?? padreDe.get(eid);
      return arriba != null ? nivelDe(arriba, prof + 1) : undefined;
    };

    // 3) Nombre/marca REAL de una pieza: Tag (marca "P-01") o un Name que no sea el patrón de Revit.
    const nombreDeInstancia = (eid: number, l: any = linea(eid)): string | undefined => {
      const tag = (l?.Tag?.value ?? '').toString().trim();
      // Un Tag que es un número o un identificador de máquina (GUID) no es una marca: va el Name del modelo.
      if (tag && !/^\d+$/.test(tag) && !pareceIdentificadorIfc(tag)) return normNombre(tag);
      const nm = (l?.Name?.value ?? '').toString().trim();
      if (nm && !/:\s*\d+\s*$/.test(nm)) return normNombre(nm);
      return undefined;
    };

    /** La obra (IfcFacility) de una pieza: subiendo por contención y agregación. */
    const obraDe = (eid: number, prof = 0): number | undefined => {
      if (instalacionDeId.has(eid)) return eid;
      if (prof > 12) return undefined;
      const arriba = contenidoEn.get(eid) ?? padreDe.get(eid);
      return arriba != null ? obraDe(arriba, prof + 1) : undefined;
    };
    const instalacionDe = (eid: number): string | undefined => {
      const o = obraDe(eid);
      return o != null ? instalacionDeId.get(o) : undefined;
    };

    // 4) QUÉ se inventaría: todo IfcElement del modelo (con sus subclases, según el esquema de web-ifc),
    //    no una lista de clases conocidas. Antes era una lista fija de ~20 clases de edificación y todo lo
    //    demás —instalaciones, refuerzo, infraestructura IFC 4.3— desaparecía de la lista aunque se viera
    //    en 3D. Quedan fuera los vanos (IfcFeatureElementSubtraction: huecos, excavaciones que restan) y
    //    los elementos virtuales (IfcVirtualElement), que no son obra; lo espacial (IfcSpace, IfcSite…) no
    //    es IfcElement. Y los CONTENEDORES con partes (ver `contenedoresConPartes`).
    const codigoDe = new Map<number, number>();
    const inventariables = new Set<number>();
    for (const eid of idsDe(WebIFC.IFCELEMENT, true)) {
      const t = tipoDeLinea(eid);
      if (t == null) continue;
      if (esDe(t, WebIFC.IFCFEATUREELEMENTSUBTRACTION) || esDe(t, WebIFC.IFCVIRTUALELEMENT)) continue;
      codigoDe.set(eid, t);
      inventariables.add(eid);
    }
    const tieneCuerpo = (id: number): boolean =>
      (linea(linea(id)?.Representation?.value)?.Representations ?? []).some(
        (r: any) => (linea(r?.value)?.RepresentationIdentifier?.value ?? '') === 'Body',
      );
    for (const c of contenedoresConPartes(padreDe, inventariables, tieneCuerpo)) inventariables.delete(c);
    // Cada pieza, a su clase canónica (`claseCanonica`), y las clases en orden estable.
    const porClase = new Map<string, { code: number; ids: number[] }>();
    for (const eid of inventariables) {
      const t = codigoDe.get(eid)!;
      const sup = supertiposDe(t);
      const clase = claseCanonica(nombreDeClase(t), sup[0]);
      const code = clase === nombreDeClase(t) ? t : Number(WebIFC[clase.toUpperCase()] ?? t);
      const e = porClase.get(clase) ?? porClase.set(clase, { code, ids: [] }).get(clase)!;
      e.ids.push(eid);
      claseDe.set(eid, clase);
    }
    const clasesOrdenadas = [...porClase.keys()].sort();

    // Una capa por TIPO dentro de cada clase. Las medidas se completan en `cerrar()`, cuando ya se
    // tiene la geometría con qué contrastar las cantidades declaradas.
    let diagHecho = false;
    const sinTipoDeclarado = new Set<number>();
    // De dónde salió el nombre de tipo de cada pieza (para `tipoDelModelo`: solo 'tipo' y 'objectType' permiten
    // que la memoria lo reconozca sola), y, en los grupos que salen aparte por uso, el nombre SIN el « · USO».
    const origenDeNombre = new Map<number, OrigenDeTipo>();
    const nombreBaseDeGrupo = new Map<string, string>();
    const etiquetasUsadas = new Set<string>();
    const usoDeLaPieza = (eid: number): string => {
      const v = (linea(eid)?.PredefinedType?.value ?? '').toString().trim().toUpperCase();
      return v && v !== 'NOTDEFINED' && v !== 'USERDEFINED' ? v : '';
    };
    const total = Math.max(1, inventariables.size);
    let hechas = 0;
    for (const clase of clasesOrdenadas) {
      const { code, ids } = porClase.get(clase)!;
      const supertipos = supertiposDe(code);
      const porTipo = new Map<string, number[]>();
      for (const eid of ids) {
        // Nombre de TIPO: (1) IfcTypeObject; (2) ObjectType del elemento; (3) nombre de instancia sin
        // los ids del final; (4) la clase. Todo sale del modelo.
        let tn = elemAType.get(eid) ?? '';
        let origen: OrigenDeTipo = 'tipo';
        if (!tn) {
          const l = linea(eid);
          const ot = normNombre(l?.ObjectType?.value ?? '');
          // Ni IfcTypeObject ni ObjectType: el modelo no dice su tipo (lo ve el chequeo del plano).
          if (!ot && !elemATipoId.has(eid)) sinTipoDeclarado.add(eid);
          if (ot) {
            tn = tipoDesdeInstancia(ot);
            if (tn) origen = 'objectType';
          }
          if (!tn) {
            const nm = (l?.Name?.value ?? '').trim();
            tn = nm ? tipoDesdeInstancia(nm) : '';
            if (tn) origen = 'nombre';
          }
        }
        if (!tn) {
          tn = clase;
          origen = 'clase';
        }
        origenDeNombre.set(eid, origen);
        (porTipo.get(tn) ?? porTipo.set(tn, []).get(tn)!).push(eid);
      }
      // Un tipo del programa puede juntar piezas de distinto USO (en FZK-Haus el mismo tipo de losa es
      // la de entrepiso y las dos de azotea): se cotizan distinto (la azotea lleva impermeabilización),
      // así que se separan por PredefinedType. La mayoría conserva el nombre del tipo —lo ya asignado
      // sigue valiendo— y la minoría sale aparte con su uso en el nombre.
      for (const [tn, eids] of [...porTipo]) {
        const porUso = new Map<string, number[]>();
        for (const eid of eids) {
          const uso = usoDeLaPieza(eid);
          (porUso.get(uso) ?? porUso.set(uso, []).get(uso)!).push(eid);
        }
        const declarados = [...porUso.keys()].filter(Boolean);
        if (declarados.length < 2) continue;
        const mayor = declarados.reduce((m, u) => (porUso.get(u)!.length > porUso.get(m)!.length ? u : m));
        const quedan = [...porUso.get(mayor)!, ...(porUso.get('') ?? [])];
        porTipo.set(tn, quedan);
        for (const u of declarados) {
          if (u === mayor) continue;
          porTipo.set(`${tn} · ${u}`, porUso.get(u)!);
          nombreBaseDeGrupo.set(`${tn} · ${u}`, tn);
        }
      }
      if (!diagHecho && clase === 'IfcWall') {
        diagHecho = true;
        console.info(`[medicionIfc] Cantidades declaradas — ${clase} #${ids[0]}:`, leerCantidades(ids[0]!));
      }
      for (const [tipo, eids] of [...porTipo].sort((x, y) => x[0].localeCompare(y[0]))) {
        let etiqueta = tipo;
        if (etiquetasUsadas.has(etiqueta)) etiqueta = `${etiqueta} · ${clase}`;
        etiquetasUsadas.add(etiqueta);
        const pisos = new Set<string>();
        const zonas = new Set<string>();
        const inst = new Set<string>();
        const clasesReales = new Set<string>();
        const instancias: Instancia[] = [];
        for (const eid of eids) {
          expressAEtiqueta.set(eid, etiqueta);
          cantidadesDe.set(eid, leerCantidades(eid));
          // Una sola lectura de la línea por pieza: de ahí salen el GlobalId (identidad entre revisiones) y el nombre.
          const lineaDeLaPieza = linea(eid);
          const gid = (lineaDeLaPieza?.GlobalId?.value ?? '').toString().trim();
          instancias.push({
            id: eid,
            ...(gid ? { gid } : {}),
            nombre: nombreDeInstancia(eid, lineaDeLaPieza),
            ...(sinTipoDeclarado.has(eid) ? { sinTipo: true as const } : {}),
            // Antes de deducir niveles: lo que cuenta es si el MODELO dice en qué piso está.
            ...(nivelDe(eid) == null ? { sinNivel: true as const } : {}),
          });
          clasesReales.add(nombreDeClase(codigoDe.get(eid)!));
          const p = elemAPiso.get(eid) ?? (nivelDe(eid) != null ? pisoNombre.get(nivelDe(eid)!) : undefined);
          if (p) pisos.add(p);
          const z = elemAZona.get(eid);
          if (z) zonas.add(z);
          const f = instalacionDe(eid);
          if (f) inst.add(f);
        }
        // El tipo tal como lo declara el modelo, sin los sufijos de la etiqueta: lo reconoce la memoria de tipos.
        // Origen = el más débil de sus piezas; uso = el único PredefinedType que declaran (o '' si no dicen o dicen varios).
        const usosDelGrupo = new Set(eids.map(usoDeLaPieza).filter(Boolean));
        const origenDelGrupo = eids.reduce<OrigenDeTipo>((peor, eid) => {
          const o = origenDeNombre.get(eid) ?? 'clase';
          return ORDEN_DE_ORIGEN.indexOf(o) > ORDEN_DE_ORIGEN.indexOf(peor) ? o : peor;
        }, 'tipo');
        capas.push({
          capa: etiqueta,
          n_entidades: eids.length,
          tipos: [...clasesReales].sort(),
          categoria: clase,
          tipoDelModelo: {
            nombre: nombreBaseDeGrupo.get(tipo) ?? tipo,
            origen: origenDelGrupo,
            uso: usosDelGrupo.size === 1 ? [...usosDelGrupo][0]! : '',
          },
          nivel: colapsar(pisos, 'Varios niveles'),
          ubicacion: colapsar(zonas, 'Varias zonas'),
          medidas: { conteo: eids.length },
          instancias,
          ifc: { supertipos, instalacion: inst.size === 1 ? [...inst][0]! : null },
        });
        hechas += eids.length;
        onAvance?.(hechas / total);
      }
    }
    instalaciones.push(...new Set(instalacionDeId.values()));

    // 5) Nivel de TODO elemento con geometría (no solo los inventariados): lo usa "Ver por nivel".
    for (const eid of codigoDe.keys()) {
      const n = nivelDe(eid);
      if (n != null) nivelDeElemento.set(eid, n);
    }
    for (const eid of [...contenidoEn.keys(), ...padreDe.keys()]) {
      const n = nivelDe(eid);
      if (n != null && !nivelDeElemento.has(eid)) nivelDeElemento.set(eid, n);
    }
    for (const eid of codigoDe.keys()) {
      const o = obraDe(eid);
      if (o != null) obraDeElemento.set(eid, o);
    }
    const porNivel = new Map<number, number>();
    for (const n of nivelDeElemento.values()) porNivel.set(n, (porNivel.get(n) ?? 0) + 1);
    for (const n of niveles) n.elementos = porNivel.get(n.id) ?? 0;

    // 6) Techo, según el estándar (no por nombres): IfcRoof + sus partes, losas ROOF, recubrimientos ROOFING.
    const hijosDe = new Map<number, number[]>();
    for (const [h, p] of padreDe) (hijosDe.get(p) ?? hijosDe.set(p, []).get(p)!).push(h);
    const conDescendientes = (id: number, prof = 0) => {
      elementosDeTecho.add(id);
      if (prof < 8) for (const h of hijosDe.get(id) ?? []) conDescendientes(h, prof + 1);
    };
    for (const id of idsDe(WebIFC.IFCROOF, true)) conDescendientes(id);
    const predef = (id: number | undefined) => enumVal(id != null ? linea(id)?.PredefinedType : undefined);
    const esDeTipo = (id: number, valor: string) => {
      const p = predef(id);
      if (p === valor) return true;
      if (p && p !== 'NOTDEFINED' && p !== 'USERDEFINED') return false;
      return predef(elemATipoId.get(id)) === valor;
    };
    for (const id of idsDe(WebIFC.IFCSLAB, true)) if (esDeTipo(id, 'ROOF')) conDescendientes(id);
    for (const id of idsDe(WebIFC.IFCCOVERING, true)) if (esDeTipo(id, 'ROOFING')) conDescendientes(id);
    if (!niveles.length) for (const id of idsDe(WebIFC.IFCSLAB, true)) if (esDeTipo(id, 'FLOOR') || esDeTipo(id, 'BASESLAB')) losasDePiso.add(id);

    // Lo que el modelo dice de cada pieza para decidir su medida (`medidaQueManda`): si rellena un vano
    // y si tiene conexiones de instalación (puertos: IfcRelConnectsPortToElement en IFC2x3, IfcRelNests
    // con IfcDistributionPort en IFC4).
    const llenaVano = new Map<number, number[]>();
    for (const rid of idsDe(WebIFC.IFCRELFILLSELEMENT)) {
      const rel = linea(rid);
      const e = rel?.RelatedBuildingElement?.value;
      if (e != null) rellenaVano.add(e);
      const v = rel?.RelatingOpeningElement?.value;
      if (v != null && e != null) (llenaVano.get(v) ?? llenaVano.set(v, []).get(v)!).push(e);
    }
    // Muro → vano (IfcRelVoidsElement) → lo que lo llena: lo que la pieza descuenta (el número generador lo muestra).
    for (const rid of idsDe(WebIFC.IFCRELVOIDSELEMENT)) {
      const rel = linea(rid);
      const m = rel?.RelatingBuildingElement?.value;
      const v = rel?.RelatedOpeningElement?.value;
      if (m == null || v == null) continue;
      for (const e of llenaVano.get(v) ?? []) (alojadosDe.get(m) ?? alojadosDe.set(m, []).get(m)!).push(e);
    }
    for (const rid of idsDe(WebIFC.IFCRELCONNECTSPORTTOELEMENT)) {
      const e = linea(rid)?.RelatedElement?.value;
      if (e != null) conPuertos.add(e);
    }
    for (const rid of idsDe(WebIFC.IFCRELNESTS)) {
      const rel = linea(rid);
      const e = rel?.RelatingObject?.value;
      if (e == null || !inventariables.has(e)) continue;
      if ((rel?.RelatedObjects ?? []).some((o: any) => o?.value != null && esDe(tipoDeLinea(o.value) ?? -1, WebIFC.IFCDISTRIBUTIONPORT))) conPuertos.add(e);
    }

    // 7) Con qué densidad se pesa cada pieza (ver `densidadDeLaPieza`).
    inventariarDensidades(elemATipoId, padreDe);
    return expressAEtiqueta;
  }

  /**
   * Material y perfil de cada pieza inventariada → su densidad. Todo del estándar:
   *  - materiales: IfcRelAssociatesMaterial (de la pieza o, si no tiene, de su tipo), abriendo
   *    conjuntos de capas, perfiles, constituyentes y listas hasta cada IfcMaterial;
   *  - densidad del modelo: `MassDensity` en las propiedades del material (IFC4 Pset_MaterialCommon /
   *    IFC2X3 IfcGeneralMaterialProperties o IfcExtendedMaterialProperties), en su unidad declarada;
   *  - familia: la `Category` del material (IFC4) o el PERFIL de la pieza (I, L, U, T, C, Z, tubo son
   *    de acero; un perfil derivado se lee por su perfil de origen). Un material que el modelo usa en
   *    perfiles de acero es acero también en las piezas donde no se ve el perfil (ángulos recortados
   *    que el modelo exporta como sólido). El nombre del material no se lee;
   *  - ENSAMBLE: una pieza sin familia que es parte de un IfcElementAssembly (IfcRelAggregates) cuyas
   *    piezas con perfil son de acero es acero (`familiaHeredadaDelEnsamble`); y su material, también
   *    en las piezas de ese material fuera de un ensamble así. Así se pesan las placas de conexión de
   *    NIST, que llevan un material propio sin Category y sin perfil.
   */
  function inventariarDensidades(elemATipoId: Map<number, number>, padreDe: Map<number, number>): void {
    const nombreDePerfil = new Map<number, string>();
    for (const n of PERFILES_DE_ACERO) if (WebIFC[n] != null) nombreDePerfil.set(WebIFC[n], n);
    const perfilesDePerfil = (id: number | undefined, out: string[], prof = 0) => {
      const l = id != null && prof < 8 ? linea(id) : null;
      if (!l) return;
      if (l.ParentProfile?.value != null) return perfilesDePerfil(l.ParentProfile.value, out, prof + 1);
      if (Array.isArray(l.Profiles)) {
        for (const x of l.Profiles) perfilesDePerfil(x?.value, out, prof + 1);
        return;
      }
      out.push(nombreDePerfil.get(l.type) ?? 'OTRO');
    };
    // Perfiles de la geometría: barridos (SweptArea), piezas mapeadas y recortes (FirstOperand).
    const perfilesDeItem = (id: number | undefined, out: string[], prof = 0) => {
      const it = id != null && prof < 12 ? linea(id) : null;
      if (!it) return;
      if (it.SweptArea?.value != null) return perfilesDePerfil(it.SweptArea.value, out);
      if (it.MappingSource?.value != null) {
        const rep = linea(linea(it.MappingSource.value)?.MappedRepresentation?.value);
        for (const x of rep?.Items ?? []) perfilesDeItem(x?.value, out, prof + 1);
        return;
      }
      if (it.FirstOperand?.value != null) perfilesDeItem(it.FirstOperand.value, out, prof + 1);
      // Un sólido de caras (Brep) no tiene perfil: no dice nada del material.
    };
    const perfilesDeGeometria = (eid: number): string[] => {
      const out: string[] = [];
      const pds = linea(linea(eid)?.Representation?.value);
      for (const r of pds?.Representations ?? []) for (const x of linea(r?.value)?.Items ?? []) perfilesDeItem(x?.value, out);
      return out;
    };
    const materialesDe = (id: number | undefined, mats: number[], perfs: string[], prof = 0): void => {
      const l = id != null && prof < 6 ? linea(id) : null;
      if (!l) return;
      if (l.type === WebIFC.IFCMATERIAL) {
        mats.push(id!);
        return;
      }
      const lista = (xs: any[] | undefined) => {
        for (const x of xs ?? []) materialesDe(x?.value, mats, perfs, prof + 1);
      };
      if (l.ForLayerSet?.value != null) materialesDe(l.ForLayerSet.value, mats, perfs, prof + 1);
      else if (l.ForProfileSet?.value != null) materialesDe(l.ForProfileSet.value, mats, perfs, prof + 1);
      else if (Array.isArray(l.MaterialLayers)) lista(l.MaterialLayers);
      else if (Array.isArray(l.MaterialProfiles)) lista(l.MaterialProfiles);
      else if (Array.isArray(l.MaterialConstituents)) lista(l.MaterialConstituents);
      else if (Array.isArray(l.Materials)) lista(l.Materials);
      else {
        // Capa / perfil / constituyente: su material (y, si es perfil IFC4, su sección).
        if (l.Profile?.value != null) perfilesDePerfil(l.Profile.value, perfs);
        if (l.Material?.value != null) materialesDe(l.Material.value, mats, perfs, prof + 1);
      }
    };
    // Espesor del tipo = suma de las capas de su juego de capas de material (IfcMaterialLayerSet), en mm. null si no
    // trae capas con espesor (puertas, ventanas, perfiles…). Solo para reconocer el tipo en otros proyectos.
    const espesorDeCapasMm = (id: number | undefined, prof = 0): number | null => {
      const l = id != null && prof < 6 ? linea(id) : null;
      if (!l) return null;
      if (l.ForLayerSet?.value != null) return espesorDeCapasMm(l.ForLayerSet.value, prof + 1);
      if (!Array.isArray(l.MaterialLayers)) return null;
      let total = 0;
      for (const x of l.MaterialLayers) {
        const grosor = num(linea(x?.value)?.LayerThickness);
        if (grosor != null && grosor > 0) total += grosor;
      }
      const mm = Math.round(total * factores.longitud * 1000);
      return mm > 0 ? mm : null;
    };
    const asociado = new Map<number, { mats: number[]; perfs: string[]; espMm: number | null }>();
    for (const rid of idsDe(WebIFC.IFCRELASSOCIATESMATERIAL)) {
      const rel = linea(rid);
      const mats: number[] = [];
      const perfs: string[] = [];
      materialesDe(rel?.RelatingMaterial?.value, mats, perfs);
      const espMm = espesorDeCapasMm(rel?.RelatingMaterial?.value);
      for (const o of rel?.RelatedObjects ?? []) if (o?.value != null) asociado.set(o.value, { mats: [...new Set(mats)], perfs, espMm });
    }
    // Densidad declarada por el modelo, por material (0 o negativa = no declarada).
    const densModelo = new Map<number, number>();
    const tomarDensidad = (matId: number | undefined, v: number | undefined) => {
      if (matId != null && v != null && v > 0 && !densModelo.has(matId)) densModelo.set(matId, v * factores.densidad);
    };
    for (const code of [WebIFC.IFCMATERIALPROPERTIES, WebIFC.IFCEXTENDEDMATERIALPROPERTIES, WebIFC.IFCGENERALMATERIALPROPERTIES]) {
      for (const id of idsDe(code)) {
        const l = linea(id);
        const matId = l?.Material?.value;
        tomarDensidad(matId, num(l?.MassDensity));
        for (const pr of [...(l?.Properties ?? []), ...(l?.ExtendedProperties ?? [])]) {
          const p = linea(pr?.value);
          if ((p?.Name?.value ?? '') === 'MassDensity') tomarDensidad(matId, num(p?.NominalValue));
        }
      }
    }
    const piezas: { eid: number; mats: number[]; famPerfil: FamiliaMaterialIfc | null }[] = [];
    const famPorPerfilDeMaterial = new Map<number, FamiliaMaterialIfc>();
    for (const capa of capas) {
      const espesores: (number | null)[] = [];
      for (const inst of capa.instancias ?? []) {
        const a = asociado.get(inst.id) ?? (elemATipoId.has(inst.id) ? asociado.get(elemATipoId.get(inst.id)!) : undefined);
        const famPerfil = familiaDePerfiles([...perfilesDeGeometria(inst.id), ...(a?.perfs ?? [])]);
        const mats = a?.mats ?? [];
        if (!mats.length) inst.sinMaterial = true;
        piezas.push({ eid: inst.id, mats, famPerfil });
        if (famPerfil && mats.length === 1) famPorPerfilDeMaterial.set(mats[0]!, famPerfil);
        espesores.push(a?.espMm ?? null);
      }
      // El espesor entra a la identidad del tipo solo si TODAS sus piezas lo declaran y coinciden.
      if (capa.tipoDelModelo && espesores.length > 0 && espesores[0] != null && espesores.every((e) => e === espesores[0])) {
        capa.tipoDelModelo = { ...capa.tipoDelModelo, espesorMm: espesores[0] };
      }
    }
    const famPorEnsambleDeMaterial = new Map<number, FamiliaMaterialIfc | null>();
    const famDeMaterial = (m: number): FamiliaMaterialIfc | null =>
      familiaDeCategoria(linea(m)?.Category?.value) ?? famPorPerfilDeMaterial.get(m) ?? null;
    const familiaDeMateriales = (mats: number[], de: (m: number) => FamiliaMaterialIfc | null | undefined) => {
      const fams = mats.map(de);
      return fams.length && fams.every((f) => f != null && f === fams[0]) ? fams[0]! : null;
    };
    const propia = new Map<number, { famMat: FamiliaMaterialIfc | null; famPerfil: FamiliaMaterialIfc | null }>();
    for (const { eid, mats, famPerfil } of piezas) propia.set(eid, { famMat: familiaDeMateriales(mats, famDeMaterial), famPerfil });

    // Ensambles: padre IfcElementAssembly → sus piezas inventariadas.
    const piezasDeEnsamble = new Map<number, number[]>();
    for (const { eid } of piezas) {
      const p = padreDe.get(eid);
      if (p == null || linea(p)?.type !== WebIFC.IFCELEMENTASSEMBLY) continue;
      (piezasDeEnsamble.get(p) ?? piezasDeEnsamble.set(p, []).get(p)!).push(eid);
    }
    const heredada = new Map<number, FamiliaMaterialIfc>();
    for (const { eid, mats } of piezas) {
      const pr = propia.get(eid)!;
      if (pr.famMat || pr.famPerfil) continue;
      const p = padreDe.get(eid);
      const hermanas = p != null ? piezasDeEnsamble.get(p) : undefined;
      if (!hermanas) continue;
      const f = familiaHeredadaDelEnsamble(hermanas.filter((h) => h !== eid).map((h) => propia.get(h)!));
      if (!f) continue;
      heredada.set(eid, f);
      // Su material queda con esa familia (si todas las piezas que lo heredan coinciden).
      if (mats.length === 1) {
        const m = mats[0]!;
        const prev = famPorEnsambleDeMaterial.get(m);
        famPorEnsambleDeMaterial.set(m, prev === undefined || prev === f ? f : null);
      }
    }

    for (const { eid, mats } of piezas) {
      const pr = propia.get(eid)!;
      const deEnsamble = heredada.get(eid) ?? familiaDeMateriales(mats, (m) => famPorEnsambleDeMaterial.get(m));
      const d = densidadDeLaPieza(
        mats.map((m) => densModelo.get(m) ?? null),
        pr.famMat ?? pr.famPerfil ?? deEnsamble,
        parametros,
      );
      if (d) densidadDe.set(eid, !pr.famMat && !pr.famPerfil && deEnsamble && d.origen === 'familia' ? { ...d, heredadaDelEnsamble: true } : d);
      else {
        sinDensidad.push({
          eid,
          materiales: mats.map((m) => ({
            nombre: (linea(m)?.Name?.value as string | undefined) ?? null,
            familia: famDeMaterial(m) ?? famPorEnsambleDeMaterial.get(m) ?? null,
            conDensidad: densModelo.has(m),
          })),
        });
      }
    }
  }

  /**
   * Los nombres de material que el modelo no tipifica, de las piezas que quedaron sin densidad
   * (`listaDeMaterialesSinTipo`). Vacío = no hace falta preguntarle nada a la IA. Después de `inventariar`.
   */
  function materialesSinTipo(): { nombre: string; piezas: number; expressIds?: number[] }[] {
    return listaDeMaterialesSinTipo(sinDensidad);
  }

  /**
   * Lo que la IA reconoció en el nombre de esos materiales → la densidad de su familia (calibración) para
   * las piezas que no la tenían (`familiaPorNombreDeMaterial`). Antes de `cerrar`. El peso que salga así
   * queda estimado y con su motivo (`pesoDeducido`).
   */
  function aplicarTiposDeMaterial(materiales: readonly { nombre: string; tipo: TipoMaterialIfc }[]): void {
    const tipos = new Map<string, TipoMaterialIfc>();
    for (const m of materiales) tipos.set(normalizarNombreDeMaterial(m.nombre), m.tipo);
    materialesReconocidos = materialesSinTipo()
      .filter((m) => tipos.has(normalizarNombreDeMaterial(m.nombre)))
      .map((m) => ({ ...m, tipo: tipos.get(normalizarNombreDeMaterial(m.nombre))! }));
    for (const { eid, materiales: mats } of sinDensidad) {
      if (densidadDe.has(eid)) continue;
      const f = familiaPorNombreDeMaterial(mats, tipos);
      if (!f) continue;
      densidadDe.set(eid, {
        densidad: densidadDeFamiliaIfc(parametros, f.familia),
        origen: 'nombre',
        familia: f.familia,
        deducido: { material: f.material, tipo: f.tipo },
      });
    }
  }

  function acumular(eid: number, pos: ArrayLike<number>, idx: ArrayLike<number>, t: ArrayLike<number>): void {
    let acc = geomDe.get(eid);
    if (!acc) {
      acc = nuevoAcumulado();
      geomDe.set(eid, acc);
    }
    acumularMalla(acc, pos, idx, t);
  }

  /**
   * Volumen de una pieza por su DEFINICIÓN, para cuando su malla no es un sólido: Σ área del perfil ×
   * profundidad de cada IfcExtrudedAreaSolid de su cuerpo (por la escala de la pieza mapeada), en m³. Es
   * exacto aunque la malla no cierre. null si el cuerpo tiene algo que no sea una extrusión de un perfil
   * medible, un recorte booleano, o si la pieza tiene vanos (IfcRelVoidsElement): lo que se recorta no se
   * descuenta y no sería exacto (un muro de FZK con ventanas daba hasta 43 % de más).
   */
  let conVanos: Set<number> | null = null;
  function volumenPorPerfil(eid: number): number | null {
    if (!conVanos) {
      conVanos = new Set();
      for (const rid of idsDe(WebIFC.IFCRELVOIDSELEMENT)) {
        const e = linea(rid)?.RelatingBuildingElement?.value;
        if (e != null) conVanos.add(e);
      }
    }
    if (conVanos.has(eid)) return null;
    const valor = (x: any): number => (typeof x === 'number' ? x : typeof x?.value === 'number' ? x.value : NaN);
    const puntos2D = (id: number | undefined): number[] | null => {
      const c = linea(id);
      if (!c) return null;
      if (c.type === WebIFC.IFCPOLYLINE)
        return (c.Points ?? []).flatMap((r: any) => (linea(r?.value)?.Coordinates ?? []).slice(0, 2).map(valor));
      if (c.type === WebIFC.IFCINDEXEDPOLYCURVE)
        return (linea(c.Points?.value)?.CoordList ?? []).flatMap((par: any[]) => (par ?? []).slice(0, 2).map(valor));
      return null;
    };
    const perfilDe = (id: number | undefined, prof = 0): { perfil: PerfilIfc; escala: number } | null => {
      const l = prof < 6 ? linea(id) : null;
      if (!l) return null;
      const t = l.type;
      if (t === WebIFC.IFCDERIVEDPROFILEDEF) {
        const op = linea(l.Operator?.value);
        const base = perfilDe(l.ParentProfile?.value, prof + 1);
        const e = Number.isFinite(valor(op?.Scale)) ? valor(op?.Scale) : 1;
        return base ? { perfil: base.perfil, escala: base.escala * e * e } : null;
      }
      if (t === WebIFC.IFCRECTANGLEHOLLOWPROFILEDEF)
        return { perfil: { tipo: 'rectanguloHueco', x: valor(l.XDim), y: valor(l.YDim), espesor: valor(l.WallThickness) }, escala: 1 };
      if (t === WebIFC.IFCRECTANGLEPROFILEDEF || t === WebIFC.IFCROUNDEDRECTANGLEPROFILEDEF)
        return { perfil: { tipo: 'rectangulo', x: valor(l.XDim), y: valor(l.YDim) }, escala: 1 };
      if (t === WebIFC.IFCCIRCLEHOLLOWPROFILEDEF)
        return { perfil: { tipo: 'circuloHueco', radio: valor(l.Radius), espesor: valor(l.WallThickness) }, escala: 1 };
      if (t === WebIFC.IFCCIRCLEPROFILEDEF) return { perfil: { tipo: 'circulo', radio: valor(l.Radius) }, escala: 1 };
      if (t === WebIFC.IFCISHAPEPROFILEDEF)
        return {
          perfil: { tipo: 'I', ancho: valor(l.OverallWidth), alto: valor(l.OverallDepth), alma: valor(l.WebThickness), ala: valor(l.FlangeThickness) },
          escala: 1,
        };
      if (t === WebIFC.IFCLSHAPEPROFILEDEF)
        return {
          perfil: { tipo: 'L', alto: valor(l.Depth), ancho: Number.isFinite(valor(l.Width)) ? valor(l.Width) : valor(l.Depth), espesor: valor(l.Thickness) },
          escala: 1,
        };
      if (t === WebIFC.IFCCENTERLINEPROFILEDEF) {
        const pts = puntos2D(l.Curve?.value);
        if (!pts) return null;
        let largo = 0;
        for (let i = 2; i + 1 < pts.length; i += 2) largo += Math.hypot(pts[i]! - pts[i - 2]!, pts[i + 1]! - pts[i - 1]!);
        return { perfil: { tipo: 'eje', largo, espesor: valor(l.Thickness) }, escala: 1 };
      }
      if (t === WebIFC.IFCARBITRARYCLOSEDPROFILEDEF || t === WebIFC.IFCARBITRARYPROFILEDEFWITHVOIDS) {
        const exterior = puntos2D(l.OuterCurve?.value);
        if (!exterior) return null;
        const huecos: number[][] = [];
        for (const v of l.InnerCurves ?? []) {
          const h = puntos2D(v?.value);
          if (!h) return null;
          huecos.push(h);
        }
        return { perfil: { tipo: 'poligono', exterior, huecos }, escala: 1 };
      }
      return null; // perfil sin fórmula: no se inventa
    };
    let total = 0;
    let hay = false;
    const items = (id: number | undefined, escala: number, prof = 0): boolean => {
      const it = prof < 12 ? linea(id) : null;
      if (!it) return false;
      if (it.type === WebIFC.IFCMAPPEDITEM) {
        const op = linea(it.MappingTarget?.value);
        const e = Number.isFinite(valor(op?.Scale)) ? valor(op?.Scale) : 1;
        const e2 = Number.isFinite(valor(op?.Scale2)) ? valor(op?.Scale2) : e;
        const e3 = Number.isFinite(valor(op?.Scale3)) ? valor(op?.Scale3) : e;
        const rep = linea(linea(it.MappingSource?.value)?.MappedRepresentation?.value);
        return (rep?.Items ?? []).every((x: any) => items(x?.value, escala * Math.abs(e * e2 * e3), prof + 1));
      }
      if (it.type !== WebIFC.IFCEXTRUDEDAREASOLID) return false; // un recorte, una malla, una revolución…
      const p = perfilDe(it.SweptArea?.value);
      const a = p ? areaDePerfilIfc(p.perfil) : null;
      // Una extrusión oblicua avanza `Depth` en su dirección: la altura del prisma es Depth × cos.
      const dir = (linea(it.ExtrudedDirection?.value)?.DirectionRatios ?? []).map(valor);
      const dl = Math.hypot(dir[0] ?? 0, dir[1] ?? 0, dir[2] ?? 0);
      const d = valor(it.Depth) * (dl > 0 ? Math.abs(dir[2] ?? 0) / dl : 1);
      if (a == null || !(d > 0)) return false;
      total += a * p!.escala * d * escala;
      hay = true;
      return true;
    };
    const reps = linea(linea(eid)?.Representation?.value)?.Representations ?? [];
    for (const r of reps) {
      const rep = linea(r?.value);
      if ((rep?.RepresentationIdentifier?.value ?? '') !== 'Body') continue;
      for (const x of rep?.Items ?? []) if (!items(x?.value, 1)) return null;
    }
    const f = factores.longitud;
    return hay && total > 0 ? total * f * f * f : null;
  }

  /**
   * Piezas que web-ifc no sabe dibujar y que se arman acá, desde su definición en el IFC, para medirlas y
   * dibujarlas. Hoy: las extrusiones de un perfil de eje (IfcCenterLineProfileDef: las láminas dobladas
   * de NIST), que web-ifc 0.0.78 descarta ("unexpected profile type") y dejaban 14 placas sin malla. Solo
   * se arman las piezas que web-ifc dejó sin ninguna malla (si mañana las dibuja, no se cuentan dos veces).
   * Los recortes booleanos (IfcBooleanClippingResult) no se aplican: se arma el sólido entero, y la
   * medida queda estimada como toda medida que sale de la geometría.
   */
  let reconstruidas: { eid: number; malla: MallaPlana; t: Matriz4 }[] | null = null;
  function reconstruirSinMalla(): { eid: number; malla: MallaPlana; t: Matriz4 }[] {
    if (reconstruidas) return reconstruidas;
    const out: { eid: number; malla: MallaPlana; t: Matriz4 }[] = [];
    reconstruidas = out;
    if (WebIFC.IFCCENTERLINEPROFILEDEF == null) return out;
    const valor = (x: any): number => (typeof x === 'number' ? x : typeof x?.value === 'number' ? x.value : 0);
    const coords = (id: number | undefined): number[] => (id != null ? (linea(id)?.Coordinates ?? []).map(valor) : []);
    const direccion = (id: number | undefined): number[] | null =>
      id != null ? (linea(id)?.DirectionRatios ?? []).map(valor) : null;
    const colocacion3D = (id: number | undefined): Matriz4 => {
      const l = id != null ? linea(id) : null;
      if (!l) return IDENTIDAD;
      return matrizDeColocacion(coords(l.Location?.value), direccion(l.Axis?.value), direccion(l.RefDirection?.value));
    };
    const colocacionLocal = (id: number | undefined, prof = 0): Matriz4 => {
      const lp = id != null && prof < 30 ? linea(id) : null;
      if (!lp) return IDENTIDAD;
      const arriba = lp.PlacementRelTo?.value != null ? colocacionLocal(lp.PlacementRelTo.value, prof + 1) : IDENTIDAD;
      return multiplicar(arriba, colocacion3D(lp.RelativePlacement?.value));
    };
    const operador = (id: number | undefined): Matriz4 => {
      const l = id != null ? linea(id) : null;
      if (!l) return IDENTIDAD;
      const e = num(l.Scale) ?? 1;
      return matrizDeOperador(
        coords(l.LocalOrigin?.value),
        direccion(l.Axis1?.value),
        direccion(l.Axis2?.value),
        direccion(l.Axis3?.value),
        e,
        num(l.Scale2) ?? e,
        num(l.Scale3) ?? e,
      );
    };
    /** Los puntos (x,y) de la línea de eje: IfcPolyline o IfcIndexedPolyCurve (sus puntos en orden). */
    const puntosDeCurva = (id: number | undefined): number[] | null => {
      const c = id != null ? linea(id) : null;
      if (!c) return null;
      if (c.type === WebIFC.IFCPOLYLINE) return (c.Points ?? []).flatMap((r: any) => coords(r?.value).slice(0, 2));
      if (c.type === WebIFC.IFCINDEXEDPOLYCURVE) {
        const lista = linea(c.Points?.value)?.CoordList ?? [];
        return lista.flatMap((par: any[]) => (par ?? []).slice(0, 2).map(valor));
      }
      return null;
    };
    const items = (id: number | undefined, M: Matriz4, acc: { malla: MallaPlana; t: Matriz4 }[], prof = 0): void => {
      const it = id != null && prof < 12 ? linea(id) : null;
      if (!it) return;
      if (it.type === WebIFC.IFCMAPPEDITEM) {
        const src = linea(it.MappingSource?.value);
        const Mn = multiplicar(multiplicar(M, operador(it.MappingTarget?.value)), colocacion3D(src?.MappingOrigin?.value));
        for (const x of linea(src?.MappedRepresentation?.value)?.Items ?? []) items(x?.value, Mn, acc, prof + 1);
        return;
      }
      if (it.FirstOperand?.value != null) return items(it.FirstOperand.value, M, acc, prof + 1);
      if (it.type !== WebIFC.IFCEXTRUDEDAREASOLID) return;
      const perfil = linea(it.SweptArea?.value);
      if (perfil?.type !== WebIFC.IFCCENTERLINEPROFILEDEF) return;
      const d = direccion(it.ExtrudedDirection?.value);
      const malla = mallaDePerfilDeEje(
        puntosDeCurva(perfil.Curve?.value) ?? [],
        valor(perfil.Thickness),
        [d?.[0] ?? 0, d?.[1] ?? 0, d?.[2] ?? 1],
        valor(it.Depth),
      );
      if (malla) acc.push({ malla, t: multiplicar(M, colocacion3D(it.Position?.value)) });
    };
    let coordinacion: Matriz4 = IDENTIDAD;
    try {
      const c = api.GetCoordinationMatrix?.(modelID);
      if (c && c.length === 16) coordinacion = Array.from(c as ArrayLike<number>);
    } catch {
      /* sin matriz de coordinación: identidad */
    }
    const aMundo = multiplicar(coordinacion, matrizAWebIfc(factores.longitud));
    for (const capa of capas) {
      for (const inst of capa.instancias ?? []) {
        if (geomDe.has(inst.id)) continue;
        const l = linea(inst.id);
        const piezas: { malla: MallaPlana; t: Matriz4 }[] = [];
        for (const r of linea(l?.Representation?.value)?.Representations ?? []) {
          const rep = linea(r?.value);
          if ((rep?.RepresentationIdentifier?.value ?? '') !== 'Body') continue;
          for (const x of rep?.Items ?? []) items(x?.value, IDENTIDAD, piezas);
        }
        if (!piezas.length) continue;
        const colocacion = multiplicar(aMundo, colocacionLocal(l?.ObjectPlacement?.value));
        for (const pz of piezas) {
          const t = multiplicar(colocacion, pz.t);
          acumular(inst.id, pz.malla.pos, pz.malla.idx, t);
          out.push({ eid: inst.id, malla: pz.malla, t });
        }
      }
    }
    return out;
  }

  /**
   * Modelo SIN pisos ni partes (GNI_model_5_arc: 0 IfcBuildingStorey): los niveles se deducen de la cara
   * superior de sus losas de piso, ya en las coordenadas de la geometría (las mismas que usa el corte).
   * Cada losa de piso es del nivel de su cara superior; cualquier otra pieza, del nivel que queda bajo su
   * base. La tolerancia es de la calibración. Sin losas de piso, el modelo queda sin niveles.
   */
  function deducirNiveles(): void {
    if (niveles.length || !losasDePiso.size) return;
    const tol = parametros.ifc_nivel_tolerancia;
    const cotas = cotasDeNiveles(
      [...losasDePiso].map((id) => geomDe.get(id)?.yMax ?? NaN),
      tol,
    );
    if (!cotas.length) return;
    cotas.forEach((c, i) =>
      niveles.push({ id: -(i + 1), nombre: 'Deducido de las losas de piso', tipo: 'piso', elevacion: c, elementos: 0, deducido: true }),
    );
    for (const [eid, g] of geomDe) {
      if (!Number.isFinite(g.yMin)) continue;
      const k = nivelPorBase(losasDePiso.has(eid) ? g.yMax : g.yMin, cotas, tol);
      nivelDeElemento.set(eid, niveles[k]!.id);
      niveles[k]!.elementos++;
    }
  }

  function cerrar(): ResultadoMedicion {
    reconstruirSinMalla();
    deducirNiveles();
    const geometria = new Map<number, Medidas>();
    const dimsDe = new Map<number, [number, number, number] | null>();
    for (const capa of capas) {
      for (const inst of capa.instancias ?? []) {
        const g = geomDe.get(inst.id);
        // web-ifc YA entrega la colocación en METROS: su flatTransformation trae la escala de unidades
        // (medido: en BasicHouse, en mm, la matriz tiene 0,001 en la diagonal y traslaciones en m). Por
        // eso la geometría se mide con factor 1; solo lo DECLARADO se convierte con `factores`.
        const geo = g
          ? medidasGeometricas(g, 1, parametros.ifc_cobertura_cara_plana, parametros.ifc_forma_lineal_min,
              claseDe.get(inst.id) === 'IfcRailing')
          : {};
        if (g) dimsDe.set(inst.id, dimensionesOrientadas(g.puntos));
        // Números generadores: largo y ancho en planta y alto de la pieza (su caja), y lo que aloja.
        if (g && Number.isFinite(g.yMin) && Number.isFinite(g.yMax)) {
          const rect = rectanguloMinimo(cascoConvexo(g.planta));
          const r3 = (n: number) => Math.round(n * 1000) / 1000;
          if (rect.largo > 0) inst.dims = { largo: r3(rect.largo), ancho: r3(rect.ancho), alto: r3(Math.max(0, g.yMax - g.yMin)) };
        }
        const aloja = alojadosDe.get(inst.id);
        if (aloja?.length) inst.alojados = [...new Set(aloja)];
        // El centro de la caja (para emparejar la pieza entre dos revisiones del modelo) antes de soltar los vértices.
        // OJO: es comparable entre revisiones solo porque el modelo se abre sin COORDINATE_TO_ORIGIN (ifc.worker.ts);
        // con esa opción cada revisión se recentraría distinto y el emparejamiento por centro dejaría de servir.
        if (g && g.puntos.length >= 3) {
          let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
          for (let k = 0; k + 2 < g.puntos.length; k += 3) {
            const X = g.puntos[k]!, Y = g.puntos[k + 1]!, Z = g.puntos[k + 2]!;
            if (X < x0) x0 = X; if (X > x1) x1 = X;
            if (Y < y0) y0 = Y; if (Y > y1) y1 = Y;
            if (Z < z0) z0 = Z; if (Z > z1) z1 = Z;
          }
          const mm = (n: number) => Math.round(n * 1000) / 1000;
          if ([x0, y0, z0, x1, y1, z1].every(Number.isFinite)) inst.centro = [mm((x0 + x1) / 2), mm((y0 + y1) / 2), mm((z0 + z1) / 2)];
        }
        if (g) {
          g.puntos = []; // ya medido: se liberan los vértices y la huella
          g.trisPlanta = [];
          g.pendPlanta = [];
        }
        geometria.set(inst.id, geo);
        const m = medidasDeLaPieza(cantidadesDe.get(inst.id) ?? [], geo, ventana);
        inst.area = m.area;
        inst.longitud = m.longitud;
        // Volumen, en este orden: el que declara el modelo; el de la malla si es un sólido; si la malla no
        // cierra, área del perfil × profundidad de su extrusión (exacto); si nada de eso, sin medir.
        if (m.volumen != null) inst.origenVolumen = m.estimadas.includes('volumen') ? 'malla' : 'declarado';
        else if (g?.abierta) {
          const vp = volumenPorPerfil(inst.id);
          if (vp != null) {
            m.volumen = vp;
            m.estimadas.push('volumen');
            inst.origenVolumen = 'perfil';
          } else inst.sinVolumen = MOTIVO_SIN_SOLIDO;
        }
        inst.volumen = m.volumen;
        const w = pesoDeLaPieza(cantidadesDe.get(inst.id) ?? [], m.volumen, densidadDe.get(inst.id) ?? null, parametros.ifc_ventana_peso);
        inst.peso = w.peso;
        const dp = densidadDe.get(inst.id);
        if (w.estimado && w.peso != null && dp?.origen === 'nombre' && dp.deducido) inst.pesoDeducido = dp.deducido;
        const estimadas: MagnitudEstimable[] = w.estimado ? [...m.estimadas, 'peso'] : m.estimadas;
        if (estimadas.length) {
          inst.estimadoGeometria = true;
          inst.magnitudesEstimadas = estimadas;
        }
      }
      capa.medidas = sumarCapa(capa.instancias ?? [], capa.n_entidades);
      capa.medidas.manda = medidaQueManda(
        (capa.instancias ?? []).map((i) => ({
          forma: formaDePieza(dimsDe.get(i.id), parametros),
          declaradas: (cantidadesDe.get(i.id) ?? []).map((q) => q.magnitud).filter((m): m is FormaIfc => m !== 'peso'),
          rellenaVano: rellenaVano.has(i.id),
          conPuertos: conPuertos.has(i.id),
        })),
        magnitudesDelEstandar(capa.categoria, capa.ifc?.supertipos),
      );
    }
    return {
      capas,
      expressAEtiqueta,
      factores,
      niveles,
      nivelDeElemento,
      obras,
      obraDeElemento,
      geometria,
      elementosDeTecho,
      densidad: densidadDe,
      sinSolido: new Set([...geomDe].filter(([, g]) => g.abierta).map(([id]) => id)),
      claseDe,
      ifc: {
        esquema: nombreEsquema || null,
        instalaciones,
        aviso: esquema.preliminar ? avisoDeEsquema(nombreEsquema, esquema.canonico) : null,
        ...(materialesReconocidos.length ? { materialesDeducidos: materialesDeducidos() } : {}),
      },
    };
  }

  /** Cada material reconocido por nombre, con sus piezas y cuántas se pesaron así. */
  function materialesDeducidos(): MaterialDeducidoIfc[] {
    const pesadas = new Map<string, number>();
    for (const capa of capas)
      for (const i of capa.instancias ?? []) {
        if (!i.pesoDeducido) continue;
        const k = normalizarNombreDeMaterial(i.pesoDeducido.material);
        pesadas.set(k, (pesadas.get(k) ?? 0) + 1);
      }
    return materialesReconocidos.map((m) => ({ ...m, pesadas: pesadas.get(normalizarNombreDeMaterial(m.nombre)) ?? 0 }));
  }

  /** `claseDe` y `capas` se llenan en `inventariar` (el visor los usa para el color de respaldo). */
  return {
    factores,
    esquema,
    claseDe,
    capas,
    inventariar,
    materialesSinTipo,
    aplicarTiposDeMaterial,
    acumular,
    reconstruirSinMalla,
    volumenPorPerfil,
    cerrar,
  };
}
